import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useReducer,
} from "react";

import { LayoutComponent } from "../LayoutComponent";
import {
  Content,
  ProgressCircle,
  View,
  Text,
  TextField,
  Divider,
  DatePicker,
  Provider,
  ActionButton,
  ToastQueue,
  Button,
  DialogTrigger,
  Dialog,
  Picker,
  Item,
  Heading,
  Content as DialogContent,
  ComboBox,
  useAsyncList,
  Flex,
  DialogContainer,
} from "@adobe/react-spectrum";
import Checkmark from "@spectrum-icons/workflow/Checkmark";
import Close from "@spectrum-icons/workflow/Close";
import GraphBarVertical from "@spectrum-icons/workflow/GraphBarVertical";
import moment from "moment";
import { formatDecimalToString } from "../../../../utils/helperUtil";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { ListProjectItemRequest } from "../../../../services/soap/item/requests/ListProjectItemRequest";
import { ListProjectItemRequestOptions_ItemGrid } from "../../../../services/soap/item/requests/ListProjectItemRequestOptions";
import { ListProjectItemCalculationRequest } from "../../../../services/soap/item/requests/ListProjectItemCalculationRequest";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { useTranslation } from "react-i18next";
import { FPAData, FPADataTypes } from "../../../../infra/protected/FPA/FPAData";
import { ListFPARequest2 } from "../../../../services/soap/project/requests/ListFPARequest2";
import { GetItemRequest } from "../../../../services/soap/item/requests/GetItemRequest";
import useComonentReload from "../../../../hooks/UseComponentReload";
import { parseDate } from "@internationalized/date";
import { useDebounce } from "@uidotdev/usehooks";
import { GetProjectRequest } from "../../../../services/soap/project/requests/GetProjectRequest";
import { TreeGridComponent } from "../../../ui/TreeGridComponent/TreeGridComponent";
import {
  TreeGridColumn,
  TreeGridRow,
  ComboBoxItem,
} from "../../../ui/TreeGridComponent/types";
import { ListItemRequest } from "../../../../services/soap/item/requests/ListItemRequest";
import { UpdateProjectRequest } from "../../../../services/soap/project/requests/UpdateProjectRequest";
import { AlertIconFill } from "../../Icons/IconsLib";
import NumericTextField from "../../../ui/NumericTextField";
import { ModifyProjectItemValuesRequest } from "../../../../services/soap/item/requests/ModifyProjectItemValuesRequest";
import { UpdateProjectItemCalculationRequest } from "../../../../services/soap/item/requests/UpdateProjectItemCalculationRequest";
import { UpdateProjectItemRequest } from "../../../../services/soap/item/requests/UpdateProjectItemRequest";
import { ListCurrencyRateRequest } from "../../../../services/soap/finance/requests/ListCurrencyRateRequest";
import { GetFinanceSettingsRequest } from "../../../../services/soap/finance/requests/GetFinanceSettingsRequest";
import { usePreloadAssets } from "../../../../hooks/UsePreloadAssets";
import {
  projectCalculationReducer,
  selectionReducer,
  initialProjectCalculationState,
  initialSelectionState,
} from "./useProjectItemGrid";
import { generateProjectProductItem } from "../../../../services/soap/item/requests/CreateProjectItemOptions";
import { CreateInvoicesFromItemsRequest } from "../../../../services/soap/invoice/requests/CreateInvoicesFromItemsRequest";
import { DeleteProjectItemRequest } from "../../../../services/soap/item/requests/DeleteProjectItemRequest";
import styles from "./project_item_grid_component.module.css";
import { CreateProjectItemRequest } from "../../../../services/soap/item/requests/CreateProjectItemRequest";
import ProductSelectionModal, {
  SelectedProduct,
} from "../../../ui/ProductSelectionModal/ProductSelectionModal";
import ItemCreateUpdate from "./ItemCreateUpdate";
import { GetCodebookRequest } from "../../../../services/soap/item/requests/GetCodebookRequest";
import { GetItemTypeResponse } from "../../../../services/soap/item/responses/GetItemTypeResponse";
import { GetItemTypeRequest } from "../../../../services/soap/item/requests/GetItemTypeRequest";
import { GetCodebookResponse } from "../../../../services/soap/item/responses/GetCodebookResponse";
import { ContextNewDialog } from "../../../../components/protected/Context/ContextNewDialog";
import { ListRegisterRequest } from "../../../../services/soap/features/requests/ListRegisterRequest";
import { invokeInvoiceApp } from "../../../../utils/NavigationUtils";
import {
  mapInvoiceTypeToAppIdent,
  InvoiceType,
} from "../../../../shared/invoice/invoiceConfig";

interface ProjectItemGridProps {
  showpurchasePrice?: boolean;
  showPurchasePriceCCY?: boolean;
  showSalesPriceBCCY?: boolean;
  showCatalogPrice?: boolean;
  showInvoiceContact?: boolean;
  showControllingData?: boolean;
  showBillingDate?: boolean;
  showCustomAttributes?: boolean;
  showTrackingCode?: boolean;
  showSupplier?: boolean;
  componentTitle?: string;
  minHeight?: number;
  selectedItem?: FPAData;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ColumnDefinition {
  name: string;
  key: string;
  width?: number;
}

interface CodebookData {
  id: string;
  name: string;
  priority?: number;
}

interface AttributeData {
  filter?: number;
  name?: string;
  priority?: number;
  type?: number | string;
  values_name?: string;
  values?: string;
  codebooks?: CodebookData[];
}

function ProjectItemGrid({
  showpurchasePrice,
  showPurchasePriceCCY,
  showSalesPriceBCCY,
  showCatalogPrice,
  showInvoiceContact,
  showControllingData,
  showBillingDate,
  showCustomAttributes,
  showTrackingCode,
  showSupplier,
  componentTitle = "Business Case",
  minHeight = 64,
  selectedItem,
}: ProjectItemGridProps) {
  const { t } = useTranslation();
  const componentRef = useRef<HTMLDivElement>(null);
  const treeGridContainerRef = useRef<HTMLDivElement>(null);
  const [componentWidth, setComponentWidth] = useState<number>(0);
  const [reloadComponent] = useComonentReload();
  const {
    itemService,
    store,
    projectService,
    financeService,
    invoiceService,
    featureService,
  } = useDependency();
  const { isMobile } = useViewInfo();
  const [rowsData, setRowsData] = useState<any[]>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);

  // Track viewport height for dynamic TreeGrid sizing (production-level keyboard handling)
  const [viewportHeight, setViewportHeight] = useState<number>(() => {
    // Use visualViewport if available (better for mobile), fallback to window.innerHeight
    if (typeof window !== "undefined") {
      return window.visualViewport?.height || window.innerHeight;
    }
    return 600; // fallback
  });

  // Track container position for accurate height calculation
  const [containerTop, setContainerTop] = useState<number>(0);

  // ProductSelectionModal state for multi-add
  const [isProductSelectionModalOpen, setIsProductSelectionModalOpen] =
    useState<boolean>(false);

  // Info modal state
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);
  const [selectedInfoRow, setSelectedInfoRow] = useState<any>(null);

  // Track the ID of a new row being edited (for auto-edit mode and escape handling)
  const [editingNewRowId, setEditingNewRowId] = useState<string | null>(null);

  // Ref for TreeGrid to trigger add row
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const treeGridRef = useRef<{ addRow: () => void } | null>(null);

  //Ref for main container for checking avaialble width
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const [mainContainerWidth, setMainContainerWidth] = useState<number>(0);

  // Ref to prevent double-deletion
  const isDeleting = useRef<boolean>(false);

  // Use reducer for all related project calculation state
  const [projectState, dispatch] = useReducer(
    projectCalculationReducer,
    initialProjectCalculationState,
  );

  // Use reducer for selection state
  const [selectionState, dispatchSelection] = useReducer(
    selectionReducer,
    initialSelectionState,
  );

  // Destructure for easier access
  const {
    calculation: projectItemCalculation,
    original: originalCalculation,
    discountPercentage: discountPercentageInput,
    discountValueBccy: discountValueBccyInput,
    discountValue: discountValueInput,
    changeFlags,
    currencyRate,
    tempRate,
    tempCurrency,
    selectedCurrency,
  } = projectState;

  const { selectedIds } = selectionState;

  const [showMarginGraphic, setShowMarginGraphic] = useState<boolean>(true);

  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null,
  );
  const [selectedInvoiceToId, setSelectedInvoiceToId] = useState<string | null>(
    null,
  );
  const [providerInputValue, setProviderInputValue] = useState<string>("");
  const [invoiceToInputValue, setInvoiceToInputValue] = useState<string>("");
  const [attributesCache, setAttributesCache] = useState<
    Record<string, AttributeData[]>
  >({});
  const [activeAttributesData, setActiveAttributesData] = useState<
    AttributeData[]
  >([]);

  // Provider async list
  const providerList = useAsyncList({
    async load({ signal, filterText }) {
      if (!filterText || filterText.length < 2) {
        return { items: [] };
      }

      try {
        const list = await projectService.listFPA2(
          new ListFPARequest2(store.Server, store.SessionId, {
            limit: 50,
            offset: 0,
            flatView: true,
            fulltext: filterText + "*",
            folderFilter: {
              COL: [
                {
                  name: "fpaTypeCategories",
                  type: "any",
                  value: "com.atollon.project.fpatypecategory.organization",
                },
              ],
            },
            ORDER: {
              COL: [
                {
                  name: "name",
                  desc: false,
                },
                {
                  name: "created",
                  desc: true,
                },
              ],
            },
          }),
        );

        if (!list || list.count === 0 || !list.ITEMS) {
          return { items: [] };
        }

        const items = list.ITEMS.map((item: any) => {
          const innerItem = item.ITEM.FOLDER;
          return {
            id: innerItem.id,
            name: `${innerItem.name} (${innerItem.folderTypeName})`,
          };
        });

        return { items };
      } catch (error) {
        console.error("Error fetching provider list:", error);
        return { items: [] };
      }
    },
  });

  // Invoice To async list
  const invoiceToList = useAsyncList({
    async load({ signal, filterText }) {
      if (!filterText || filterText.length < 2) {
        return { items: [] };
      }

      try {
        const list = await projectService.listFPA2(
          new ListFPARequest2(store.Server, store.SessionId, {
            limit: 50,
            offset: 0,
            flatView: true,
            fulltext: filterText + "*",
            folderFilter: {
              COL: [
                {
                  name: "fpaTypeCategories",
                  type: "any",
                  value: "com.atollon.project.fpatypecategory.client",
                },
              ],
            },
            ORDER: {
              COL: [
                {
                  name: "name",
                  desc: false,
                },
                {
                  name: "created",
                  desc: true,
                },
              ],
            },
          }),
        );

        if (!list || list.count === 0 || !list.ITEMS) {
          return { items: [] };
        }

        const items = list.ITEMS.map((item: any) => {
          const innerItem = item.ITEM.FOLDER;
          return {
            id: innerItem.id,
            name: `${innerItem.name} (${innerItem.folderTypeName})`,
          };
        });

        return { items };
      } catch (error) {
        console.error("Error fetching invoice list:", error);
        return { items: [] };
      }
    },
  });

  const [probabilityTerm, setProbabilityTerm] = useState<string>("");
  const debouncedProbabilityTerm = useDebounce(probabilityTerm, 1000);
  const [showExchangeRateDialog, setShowExchangeRateDialog] =
    useState<boolean>(false);
  const [pendingExpectedCloseDate, setPendingExpectedCloseDate] = useState<
    string | null
  >(null);
  const { currencies } = usePreloadAssets();

  const [bccyCurrency, setBccyCurrency] = useState<string>("");

  useEffect(() => {
    const loadBccyCurrency = async () => {
      try {
        const settings = await financeService.getFinanceSettings(
          new GetFinanceSettingsRequest(store.Server, store.SessionId),
        );
        if (settings?.result === "OK" && settings?.SETTINGS?.currency) {
          const code =
            currencies.find((c: any) => c.id === settings.SETTINGS.currency)
              ?.code || "";
          setBccyCurrency(code);
        }
      } catch (e) {
        // silent - bccyCurrency stays ""
      }
    };
    loadBccyCurrency();
  }, [store.Server, store.SessionId, financeService, currencies]);

  // Currency rate dialog states
  const [showCurrencyRateDialog, setShowCurrencyRateDialog] =
    useState<boolean>(false);
  const projectRawData = useRef<any>(null);

  // --- Create Deal / Device / Project feature ---
  /** Tracks which "create project" dialog is open and which item-type bucket to use */
  const [createProjectDialogTarget, setCreateProjectDialogTarget] =
    useState<null | {
      itemTypeTarget: "salesItems" | "technologyItems" | "billingItems";
      items: any[];
    }>(null);
  /** fpaTypeCategory key fetched from the server register for the current item type */
  const projectListKeyRef = useRef<string>("");

  // Define base columns structure (memoized)
  const baseColumnsData = useMemo<any[]>(
    () => [
      {
        name: t("product_name", { ns: "layout_components" }),
        key: "productName",
      },
      {
        name: t("quantity", { ns: "layout_components" }),
        key: "quantity",
        width: 100,
      },
      {
        name: t("sold_quantity", { ns: "layout_components" }),
        key: "soldQuantity",
        width: 120,
      }, //Custom
      {
        name: t("remaining_sold", { ns: "layout_components" }),
        key: "remainingSold",
        width: 130,
      }, //Custom
      {
        name: t("purchase_quantity", { ns: "layout_components" }),
        key: "purchaseQuantity",
        width: 140,
      }, //Custom
      {
        name: t("remaining_purchase", { ns: "layout_components" }),
        key: "remainingPurchase",
        width: 160,
      }, //Custom
      {
        name: t("attrib0", { ns: "layout_components" }),
        key: "attrib0",
        width: 100,
      }, //Custom
      {
        name: t("unit_sales_price", { ns: "layout_components" }),
        key: "unitPriceCurr",
        width: 150,
      },
      {
        name: t("sales_price", { ns: "layout_components" }),
        key: "totalAmountBccy",
        width: 100,
      }, //Custom
      {
        name: t("total_sales_price", { ns: "layout_components" }),
        key: "totalPriceCurr",
        width: 140,
      },
      {
        name: t("unit_purchase_price", { ns: "layout_components" }),
        key: "purchasePriceCurr",
        width: 150,
      },
      {
        name: t("purchase_price", { ns: "layout_components" }),
        key: "totalPurchaseBccy",
        width: 150,
      }, //Custom
      {
        name: t("billing_date", { ns: "layout_components" }),
        key: "billingDateFormatted",
        width: 100,
      }, //Custom
      {
        name: t("supplier", { ns: "layout_components" }),
        key: "supplierName",
        width: 150,
      }, //Custom
      {
        name: t("ratio", { ns: "layout_components" }),
        key: "ratio",
        width: 50,
      },
    ],
    [t],
  );

  // Define base mobile columns structure (memoized)
  const baseColumnsDataMobile = useMemo<any[]>(
    () => [
      {
        name: t("product_name", { ns: "layout_components" }),
        key: "productName",
      },
      {
        name: t("quantity", { ns: "layout_components" }),
        key: "quantity",
        width: 100,
      },
      {
        name: t("sold_quantity", { ns: "layout_components" }),
        key: "soldQuantity",
        width: 120,
      }, //Custom
      {
        name: t("remaining_sold", { ns: "layout_components" }),
        key: "remainingSold",
        width: 130,
      }, //Custom
      {
        name: t("purchase_quantity", { ns: "layout_components" }),
        key: "purchaseQuantity",
        width: 140,
      }, //Custom
      {
        name: t("remaining_purchase", { ns: "layout_components" }),
        key: "remainingPurchase",
        width: 160,
      }, //Custom
      {
        name: t("attrib0", { ns: "layout_components" }),
        key: "attrib0",
        width: 100,
      }, //Custom
      {
        name: t("unit_sales_price", { ns: "layout_components" }),
        key: "unitPriceCurr",
        width: 150,
      },
      {
        name: t("sales_price", { ns: "layout_components" }),
        key: "totalAmountBccy",
        width: 100,
      }, //Custom
      {
        name: t("total_sales_price", { ns: "layout_components" }),
        key: "totalPriceCurr",
        width: 140,
      },
      {
        name: t("unit_purchase_price", { ns: "layout_components" }),
        key: "purchasePriceCurr",
        width: 150,
      },
      {
        name: t("purchase_price", { ns: "layout_components" }),
        key: "totalPurchaseBccy",
        width: 150,
      }, //Custom
      {
        name: t("billing_date", { ns: "layout_components" }),
        key: "billingDateFormatted",
        width: 100,
      }, //Custom
      {
        name: t("supplier", { ns: "layout_components" }),
        key: "supplierName",
        width: 150,
      }, //Custom
      {
        name: t("ratio", { ns: "layout_components" }),
        key: "ratio",
        width: 100,
      },
    ],
    [t],
  );

  // Memoize column visibility configuration
  const columnsToHide = useMemo(
    () => [
      { key: "totalPurchaseBccy", visible: showpurchasePrice },
      { key: "totalAmountBccy", visible: showSalesPriceBCCY },
      { key: "attrib0", visible: showCustomAttributes },
      { key: "billingDateFormatted", visible: showBillingDate },
      { key: "supplierName", visible: showSupplier },
      { key: "soldQuantity", visible: showControllingData },
      { key: "remainingSold", visible: showControllingData },
      { key: "purchaseQuantity", visible: showControllingData },
      { key: "remainingPurchase", visible: showControllingData },
    ],
    [
      showpurchasePrice,
      showSalesPriceBCCY,
      showCustomAttributes,
      showBillingDate,
      showSupplier,
      showControllingData,
    ],
  );

  // Memoize filter function
  const filterColumns = useCallback(
    (columns: any[]) =>
      columns.filter((column) =>
        columnsToHide.every((col) => col.visible || col.key !== column.key),
      ),
    [columnsToHide],
  );

  // Apply filtering with memoization
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const columnsData = useMemo(
    () => filterColumns(baseColumnsData),
    [filterColumns, baseColumnsData],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const columnsDataMobile = useMemo(
    () => filterColumns(baseColumnsDataMobile),
    [filterColumns, baseColumnsDataMobile],
  );

  // Suppress ResizeObserver loop errors (non-critical browser warning)
  useEffect(() => {
    const errorHandler = (e: ErrorEvent) => {
      if (
        e.message?.includes("ResizeObserver loop") ||
        e.message?.includes(
          "ResizeObserver loop completed with undelivered notifications",
        )
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };
    window.addEventListener("error", errorHandler);
    return () => window.removeEventListener("error", errorHandler);
  }, []);

  useEffect(() => {
    if (!componentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      // Use requestAnimationFrame to batch updates and prevent ResizeObserver loop errors
      window.requestAnimationFrame(() => {
        if (!entries.length) return;
        for (const entry of entries) {
          setComponentWidth(entry.contentRect.width);
        }
      });
    });

    resizeObserver.observe(componentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Track mainContainerRef width reactively so decisions can be made per screen size
  useEffect(() => {
    if (!mainContainerRef.current) return;

    // Read initial width immediately — offsetWidth is available synchronously
    // console.log(
    //   "mainContainerRef.current.offsetWidth",
    //   mainContainerRef.current.offsetWidth,
    // );
    const initialWidth = mainContainerRef.current.offsetWidth;
    if (initialWidth > 0) {
      setMainContainerWidth(initialWidth);
    }

    const resizeObserver = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (!entries.length) return;
        for (const entry of entries) {
          if (entry.contentRect.width > 0) {
            setMainContainerWidth(entry.contentRect.width);
          }
        }
      });
    });

    resizeObserver.observe(mainContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mainContainerRef.current]);

  // Production-level viewport height tracking for keyboard overlay handling
  // This listens to both window resize and visualViewport resize events
  useEffect(() => {
    const updateViewportHeight = () => {
      // Prefer visualViewport for accurate mobile keyboard detection
      const height = window.visualViewport?.height || window.innerHeight;
      setViewportHeight(height);

      // Also update container position when viewport changes
      if (treeGridContainerRef.current) {
        const rect = treeGridContainerRef.current.getBoundingClientRect();
        setContainerTop(rect.top);
      }
    };

    // Listen to visualViewport resize (fires when keyboard shows/hides on mobile)
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateViewportHeight);
    }

    // Fallback to window resize for older browsers
    window.addEventListener("resize", updateViewportHeight);

    // Initial update with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(updateViewportHeight, 100);

    return () => {
      clearTimeout(timeoutId);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          updateViewportHeight,
        );
      }
      window.removeEventListener("resize", updateViewportHeight);
    };
  }, []);

  // Update container position when content changes
  useEffect(() => {
    if (treeGridContainerRef.current) {
      const rect = treeGridContainerRef.current.getBoundingClientRect();
      setContainerTop(rect.top);
    }
  }, [rowsData, showLoader]);

  // Calculate responsive key based on width breakpoints to force rerender
  const responsiveKey = useMemo(() => {
    if (componentWidth === 0) return "initial";
    if (componentWidth < 768) return "mobile";
    if (componentWidth < 1024) return "tablet";
    if (componentWidth < 1440) return "desktop";
    return "large";
  }, [componentWidth]);

  // Calculate dynamic TreeGrid maxHeight based on actual viewport and container position
  // This ensures the grid remains visible even when mobile keyboard is shown
  const treeGridMaxHeight = useMemo(() => {
    // Calculate available space from container position to bottom of viewport
    // Reserve space for: buttons below grid (~60px), padding/margins (~40px)
    const bottomReservedSpace = 100;
    const availableHeight = viewportHeight - containerTop - bottomReservedSpace;

    // Set reasonable min/max constraints
    // For mobile/tablet: min 150px to show at least a few rows, max based on available space
    // For desktop: more generous sizing
    const minHeight = isMobile ? 150 : 300;
    const maxHeight = isMobile ? Math.min(availableHeight, 500) : 600;

    const calculatedHeight = Math.max(
      minHeight,
      Math.min(availableHeight, maxHeight),
    );

    // Debug logging for troubleshooting
    // if (isMobile && containerTop > 0) {
    //   console.log("TreeGrid height calculation:", {
    //     viewportHeight,
    //     containerTop,
    //     availableHeight,
    //     calculatedHeight,
    //     bottomReservedSpace,
    //   });
    // }

    return `${calculatedHeight}px`;
  }, [viewportHeight, containerTop, isMobile]);

  const loadProjectItemCalculation = useCallback(
    async (projectId: string) => {
      try {
        const response: any = await itemService.listProjectItemCalculation(
          new ListProjectItemCalculationRequest(
            store.Server,
            store.SessionId,
            undefined,
            projectId,
          ),
        );

        if (response?.result === "OK" && response?.ITEM) {
          //console.log("response.ITEM", response.ITEM);
          // Single dispatch to update all related state atomically
          dispatch({ type: "LOAD_PROJECT_ITEM", payload: response.ITEM });
        }
      } catch (error) {
        console.error("Error loading project item calculation:", error);
        ToastQueue.negative(
          t("failed_to_load_project_item_calculation", {
            ns: "layout_components",
            defaultValue: "Failed to load project item calculation",
          }),
          {
            timeout: 3000,
          },
        );
      }
    },
    [itemService, store.Server, store.SessionId, t],
  );

  useEffect(() => {
    if (!debouncedProbabilityTerm) return;
    // Only update if the debounced term is different from the original server value
    const originalProbability =
      originalCalculation?.probability?.toString().replace("%", "").trim() ||
      "";
    if (debouncedProbabilityTerm === originalProbability) return;

    // console.log('Probability update triggered by user input:', {
    //   debouncedProbabilityTerm,
    //   originalProbability
    // });

    updateProbabilityValue(debouncedProbabilityTerm).catch((error) => {
      console.error("Error updating probability value:", error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedProbabilityTerm]);

  const getItemDetail = useCallback(
    async (item: any) => {
      try {
        if (!item.partID) return "";
        const itemResp = await itemService.getItem(
          new GetItemRequest(store.Server, store.SessionId, item.partID),
        );
        return itemResp?.ITEM || {};
      } catch (error) {
        console.error("Error fetching part number:", error);
        return {};
      }
    },
    [itemService, store.Server, store.SessionId],
  );

  const updateProjectItemGridData = useCallback(
    async (projectItemGridData: any[]) => {
      if (!projectItemGridData) return [];
      return Promise.all(
        projectItemGridData.map(async (item) => {
          const itemDetail: any = await getItemDetail(item);
          //console.log("itemDetail",itemDetail)
          return {
            ...item,
            _refId: `${itemDetail.refId} (${itemDetail.name})`,
            partNumber: itemDetail.partNumber,
            productName: itemDetail.name,
          };
        }),
      );
    },
    [getItemDetail],
  );

  const setRecordIds = (record: any, selectedItem: any) => {
    switch (selectedItem?.type) {
      case FPADataTypes.ACTIVITY:
        record.folderId = selectedItem?.parent?.parent_id;
        record.projectId = String(selectedItem?.parent_id);
        record.activityId = String(selectedItem?.id);
        break;
      case FPADataTypes.PROJECT:
        record.folderId = "";
        record.projectId = String(selectedItem?.id);
        record.activityId = "";
        break;
      case FPADataTypes.FOLDER:
        record.folderId = String(selectedItem?.id);
        record.projectId = "";
        record.activityId = "";
        break;
    }
  };

  const loadAttributesData = useCallback(
    async (item_type: string): Promise<AttributeData[]> => {
      let itemTypeRes: GetItemTypeResponse = await itemService.getItemType(
        new GetItemTypeRequest(store.Server, store.SessionId, item_type),
      );
      let codebookRes: GetCodebookResponse | null = null;

      var attrib0: AttributeData = {
        filter: itemTypeRes.ITEM_TYPE.attrib0_filter,
        name: itemTypeRes.ITEM_TYPE.attrib0_name,
        priority: itemTypeRes.ITEM_TYPE.attrib0_priority,
        type: itemTypeRes.ITEM_TYPE.attrib0_type,
        values_name: itemTypeRes.ITEM_TYPE.attrib0_values_name,
        values: itemTypeRes.ITEM_TYPE.attrib0_values,
      };

      var attrib1: AttributeData = {
        filter: itemTypeRes.ITEM_TYPE.attrib1_filter,
        name: itemTypeRes.ITEM_TYPE.attrib1_name,
        priority: itemTypeRes.ITEM_TYPE.attrib1_priority,
        type: itemTypeRes.ITEM_TYPE.attrib1_type,
        values_name: itemTypeRes.ITEM_TYPE.attrib1_values_name,
        values: itemTypeRes.ITEM_TYPE.attrib1_values,
      };

      var attrib2: AttributeData = {
        filter: itemTypeRes.ITEM_TYPE.attrib2_filter,
        name: itemTypeRes.ITEM_TYPE.attrib2_name,
        priority: itemTypeRes.ITEM_TYPE.attrib2_priority,
        type: itemTypeRes.ITEM_TYPE.attrib2_type,
        values_name: itemTypeRes.ITEM_TYPE.attrib2_values_name,
        values: itemTypeRes.ITEM_TYPE.attrib2_values,
      };

      var attrib3: AttributeData = {
        filter: itemTypeRes.ITEM_TYPE.attrib3_filter,
        name: itemTypeRes.ITEM_TYPE.attrib3_name,
        priority: itemTypeRes.ITEM_TYPE.attrib3_priority,
        type: itemTypeRes.ITEM_TYPE.attrib3_type,
        values_name: itemTypeRes.ITEM_TYPE.attrib3_values_name,
        values: itemTypeRes.ITEM_TYPE.attrib3_values,
      };

      if (attrib0.type === "5" && attrib0.values) {
        codebookRes = await itemService.getCodebook(
          new GetCodebookRequest(store.Server, store.SessionId, attrib0.values),
        );
        attrib0.codebooks = codebookRes.CODEBOOK[0].VALUES.map((item: any) => {
          return {
            id: item.id,
            name: item.name,
            priority: item.priority,
          };
        });
      }

      if (attrib1.type === "5" && attrib1.values) {
        codebookRes = await itemService.getCodebook(
          new GetCodebookRequest(store.Server, store.SessionId, attrib1.values),
        );
        attrib1.codebooks = codebookRes.CODEBOOK[0].VALUES.map((item: any) => {
          return {
            id: item.id,
            name: item.name,
            priority: item.priority,
          };
        });
      }

      if (attrib2.type === "5" && attrib2.values) {
        codebookRes = await itemService.getCodebook(
          new GetCodebookRequest(store.Server, store.SessionId, attrib2.values),
        );
        attrib2.codebooks = codebookRes.CODEBOOK[0].VALUES.map((item: any) => {
          return {
            id: item.id,
            name: item.name,
            priority: item.priority,
          };
        });
      }
      // console.log('attrib0', attrib0);
      // console.log('attrib1', attrib1);
      // console.log('attrib2', attrib2);
      // console.log('attrib3', attrib3);

      return [attrib0, attrib1, attrib2, attrib3];
    },
    [itemService, store.Server, store.SessionId],
  );

  const ensureAttributesForType = useCallback(
    async (itemType: string): Promise<AttributeData[]> => {
      if (!itemType) {
        return [];
      }

      const normalizedType = String(itemType);

      if (attributesCache[normalizedType]) {
        setActiveAttributesData(attributesCache[normalizedType]);
        return attributesCache[normalizedType];
      }

      const attributes = await loadAttributesData(normalizedType);
      setAttributesCache((prev) => ({ ...prev, [normalizedType]: attributes }));
      setActiveAttributesData(attributes);
      return attributes;
    },
    [attributesCache, loadAttributesData],
  );

  // Function to load project product data
  const loadProjectItemGridData = useCallback(async () => {
    const record: any = {};
    setRecordIds(record, selectedItem);

    // Helper to format price with currency
    const formatPrice = (value: number | string, currency: string): string => {
      const numValue = typeof value === "string" ? parseFloat(value) : value;
      if (isNaN(numValue)) return `0 ${currency}`;
      return `${numValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency}`;
    };

    // Call API to fetch project product data
    const projecrProdRes: any = await itemService.listProjectItem(
      new ListProjectItemRequest(
        store.Server,
        store.SessionId,
        50,
        0,
        new ListProjectItemRequestOptions_ItemGrid(record.projectId),
      ),
    );

    // If API call is successful
    if (projecrProdRes?.result === "OK") {
      // Map the project product data to desired format
      projectRawData.current = projecrProdRes;

      if (!projecrProdRes?.ROWS || projecrProdRes.ROWS.length === 0) {
        //console.log("No project items found");
        setRowsData([]);
        return;
      }
      //console.log("projecrProdRes.ROWS", projecrProdRes.ROWS);

      const item_type = projecrProdRes.ROWS
        ? projecrProdRes.ROWS[0].ITEM.itemType
        : null;

      let attributes_data = item_type
        ? await ensureAttributesForType(item_type)
        : [];
      setActiveAttributesData(attributes_data);
      //console.log("attributes_data", attributes_data);
      // First pass: create flat items with parent reference
      const flatItems = projecrProdRes?.ROWS?.map(
        (item: any, index: number) => {
          const { ITEM } = item;
          const { currency } = ITEM;

          return {
            ...ITEM,
            id: ITEM.id,
            // Store parent ID for tree structure - similar to ProductSelectionExample
            parentId: ITEM.parentId || null,
            sortOrder: index,
            quantity: formatDecimalToString(
              parseFloat(ITEM.quantity).toFixed(2),
            ),
            partID: ITEM.item,
            _refId: "",
            ref_Id: ITEM.refId,
            partNumber: "Part Number",
            productName: ITEM.name,
            name: ITEM.name,
            supplier: ITEM.supplier,
            supplierName: ITEM.supplierName,
            currency,
            vatValue: parseFloat(ITEM.vatValue) || 0,
            vatPercent: ITEM.vatValue ? `${parseFloat(ITEM.vatValue)}%` : "0%",
            amountWithVat: ITEM.amountWithVat,
            totalAmountWithVatCurr: formatPrice(
              ITEM.totalAmountWithVat || 0,
              currency,
            ),
            unitPrice: formatDecimalToString(ITEM.amount),
            unitPriceCurr: formatPrice(ITEM.amount, currency),
            totalPrice: formatDecimalToString(
              parseFloat(ITEM.totalAmount).toFixed(2),
            ),
            totalPriceCurr: formatPrice(ITEM.totalAmount, currency),
            purchasePrice: ITEM.totalPurchase,
            purchasePriceCurr: formatPrice(ITEM.totalPurchase, currency),
            totalPurchase: ITEM.totalPurchase,
            totalPurchaseCurr: formatPrice(ITEM.totalPurchase, currency),
            totalPurchaseBccy: ITEM.totalPurchaseBccy,
            totalPurchaseBccyCurr: formatPrice(
              ITEM.totalPurchaseBccy,
              currency,
            ),
            totalAmountBccy: ITEM.totalAmountBccy,
            totalAmountBccyCurr: formatPrice(ITEM.totalAmountBccy, currency),
            totalAmountWithVat: ITEM.totalAmountWithVat,
            billingDate: ITEM.billingDate,
            billingDateFormatted: moment(ITEM.billingDate).format("MM/DD/YYYY"),
            ratio: (ITEM.ratio || "0") + "%",
            ratioValue: ITEM.ratio || "0", // Raw value for saving
            attrib0: ITEM.attrib0,
            soldQuantity: formatDecimalToString(
              parseFloat(ITEM.totalQuantity).toFixed(2),
            ),
            remainingSold: "0",
            purchaseQuantity: formatDecimalToString(
              parseFloat(ITEM.freeValue).toFixed(2),
            ),
            remainingPurchase: "0",
            currencyRate: ITEM.currencyRate,
          };
        },
      );

      // Build tree structure similar to ProductSelectionExample
      // Create a map for quick lookup
      const itemMap = new Map<string, any>();
      flatItems.forEach((item: any) => {
        item.childrenIds = [];
        itemMap.set(item.id, item);
      });

      // Calculate childrenIds for each parent
      flatItems.forEach((item: any) => {
        if (item.parentId && itemMap.has(item.parentId)) {
          const parent = itemMap.get(item.parentId);
          parent.childrenIds.push(item.id);
        }
      });

      // Calculate depth for each item (recursive)
      const calculateDepth = (item: any): number => {
        if (!item.parentId) return 0;
        const parent = itemMap.get(item.parentId);
        if (!parent) return 0;
        return 1 + calculateDepth(parent);
      };

      // Build final tree data with proper TreeGridRow structure
      const projectItemGridData = flatItems.map((item: any) => ({
        ...item,
        parentId: item.parentId || null,
        depth: calculateDepth(item),
        childrenIds: item.childrenIds || [],
        isExpanded: item.childrenIds.length > 0, // Expand parents by default
      }));

      const rate = projectItemGridData[0]?.currencyRate || "1";
      dispatch({ type: "UPDATE_CURRENCY_RATE", rate });
      dispatch({ type: "UPDATE_TEMP_RATE", rate });

      // Update project product data with additional information
      try {
        const updatedData =
          await updateProjectItemGridData(projectItemGridData);
        console.log("updatedData", updatedData);
        setRowsData(updatedData);
      } catch (error) {
        console.error("Error updating project item grid data:", error);
        // Fallback: set data without part numbers if updateProjectItemGridData fails
        setRowsData(projectItemGridData);
      }
    } else {
      console.error("API call failed:", projecrProdRes);
      setRowsData([]);
    }
  }, [
    selectedItem,
    itemService,
    store.Server,
    store.SessionId,
    updateProjectItemGridData,
    ensureAttributesForType,
  ]);

  // Search products function for ComboBox
  const searchProducts = useCallback(
    async (filterText: string): Promise<ComboBoxItem[]> => {
      if (filterText.length < 2) {
        return [];
      }

      try {
        // Search by name and refId concurrently
        const [itemResp, itemRespByRefID]: any = await Promise.all([
          itemService.listItem(
            new ListItemRequest(store.Server, store.SessionId, 15, 0, {
              searchName: filterText.trim(),
              statusShow: "1",
            }),
          ),
          itemService.listItem(
            new ListItemRequest(store.Server, store.SessionId, 15, 0, {
              refId: filterText.trim(),
              statusShow: "1",
            }),
          ),
        ]);

        const results: ComboBoxItem[] = [];

        // Format items from refId search
        if (itemRespByRefID.ITEM?.length) {
          itemRespByRefID.ITEM.forEach((item: any) => {
            results.push({
              ...item,
              id: `${item.id}_ref`,
              label: `${item.refId} (${item.name})`,
              sku: item.refId,
              unitPrice: parseFloat(item.amount) || 0,
              catalogPrice: parseFloat(item.amountWithVat) || 0,
              category: "SKU Match",
              productId: item.id,
              productName: item.name,
              amountWithVat: parseFloat(item.amountWithVat) || 0,
              vatValue: parseFloat(item.vatValue) || 0,
              purchasePrice: parseFloat(item.purchasePrice) || 0,
              currency: item.currencyName || bccyCurrency,
            });
          });
        }

        // Format items from name search
        if (itemResp.ITEM?.length) {
          itemResp.ITEM.forEach((item: any) => {
            // Avoid duplicates
            if (!results.some((r) => r.productId === item.id)) {
              results.push({
                ...item,
                id: `${item.id}_name`,
                label: item.name + (item.refId ? ` (${item.refId})` : ""),
                sku: item.refId || "",
                unitPrice: parseFloat(item.amount) || 0,
                catalogPrice: parseFloat(item.amountWithVat) || 0,
                category: "Name Match",
                productId: item.id,
                productName: item.name,
                amountWithVat: parseFloat(item.amountWithVat) || 0,
                vatValue: parseFloat(item.vatValue) || 0,
                purchasePrice: parseFloat(item.purchasePrice) || 0,
                currency: item.currencyName || bccyCurrency,
              });
            }
          });
        }

        return results;
      } catch (error) {
        console.error("Product search error:", error);
        return [];
      }
    },
    [itemService, store.Server, store.SessionId, bccyCurrency],
  );

  const getCurrencyRate = useCallback(
    async (currency_id: string, rateDate: string): Promise<number> => {
      try {
        const currency_rate: any = await financeService.listCurrencyRate(
          new ListCurrencyRateRequest(store.Server, store.SessionId, {
            currency: currency_id,
            rateDate: rateDate,
          }),
        );

        if (
          !currency_rate ||
          !currency_rate.currencyRate ||
          !currency_rate.currencyRate.rate
        ) {
          console.error("Invalid currency rate response:", currency_rate);
          ToastQueue.negative(
            t("invalid_currency_rate_response", {
              ns: "layout_components",
              defaultValue: "Invalid currency rate response",
            }),
            {
              timeout: 3000,
            },
          );
          return 1;
        }

        const rate = currency_rate.currencyRate.rate;
        dispatch({ type: "UPDATE_CURRENCY_RATE", rate });
        return rate;
      } catch (error) {
        console.error("Error fetching currency rate:", error);
        ToastQueue.negative(
          t("failed_to_fetch_currency_rate", {
            ns: "layout_components",
            defaultValue: "Failed to fetch currency rate",
          }),
          { timeout: 3000 },
        );
        return 1;
      }
    },
    [financeService, store.Server, store.SessionId, t],
  );

  const getTempCurrencyRate = useCallback(
    async (currency_id: string, rateDate: string): Promise<number> => {
      try {
        const currency_rate: any = await financeService.listCurrencyRate(
          new ListCurrencyRateRequest(store.Server, store.SessionId, {
            currency: currency_id,
            rateDate: rateDate,
          }),
        );

        if (
          !currency_rate ||
          !currency_rate.currencyRate ||
          !currency_rate.currencyRate.rate
        ) {
          console.error("Invalid currency rate response:", currency_rate);
          ToastQueue.negative(
            t("invalid_currency_rate_response", {
              ns: "layout_components",
              defaultValue: "Invalid currency rate response",
            }),
            {
              timeout: 3000,
            },
          );
          return 1;
        }
        //console.log(" currency rate response:", currency_rate);
        const rate = currency_rate.currencyRate.rate;
        dispatch({ type: "UPDATE_TEMP_RATE", rate });
        return rate;
      } catch (error) {
        console.error("Error fetching temporary currency rate:", error);
        ToastQueue.negative(
          t("failed_to_fetch_currency_rate", {
            ns: "layout_components",
            defaultValue: "Failed to fetch currency rate",
          }),
          { timeout: 3000 },
        );
        return 1;
      }
    },
    [financeService, store.Server, store.SessionId, t],
  );

  // Calculate Gross Profit and Margin dynamically
  // Gross Profit (BCCY) = Deal Value (BCCY) - Purchase Cost (BCCY) (same as Margin BCCY)
  const grossProfitBccy = useMemo(() => {
    // Use amountWithoutVatBccy from API if available (for initial load precision)
    // Otherwise calculate from amountWithoutVat * rate (for dynamic updates)
    const apiDealValueBccy =
      parseFloat(projectItemCalculation.amountWithoutVatBccy || "0") || 0;
    const dealValue = parseFloat(projectItemCalculation.amountWithoutVat) || 0;
    const rate = currencyRate ? parseFloat(currencyRate) : 1;
    const calculatedDealValueBccy = Math.round(dealValue * rate * 100) / 100;

    // Use API value if it exists and is close to calculated (no user edits), otherwise use calculated
    const dealValueBccy =
      apiDealValueBccy > 0 &&
      !changeFlags.isDiscountChanged &&
      !changeFlags.isDiscountValueChanged &&
      !changeFlags.isDealValueChanged
        ? apiDealValueBccy
        : calculatedDealValueBccy;

    const purchaseCostBccy =
      parseFloat(projectItemCalculation.purchasePriceBccy || "0") || 0;
    return Math.round((dealValueBccy - purchaseCostBccy) * 100) / 100;
  }, [
    projectItemCalculation.amountWithoutVat,
    projectItemCalculation.amountWithoutVatBccy,
    projectItemCalculation.purchasePriceBccy,
    currencyRate,
    changeFlags.isDiscountChanged,
    changeFlags.isDiscountValueChanged,
    changeFlags.isDealValueChanged,
  ]);

  const grossProfit = useMemo(() => {
    const dealValue = parseFloat(projectItemCalculation.amountWithoutVat) || 0;
    const purchaseCost =
      parseFloat(projectItemCalculation.totalPurchase || "0") || 0;
    return dealValue - purchaseCost;
  }, [projectItemCalculation]);

  const marginPercentage = useMemo(() => {
    // Use amountWithoutVatBccy from API if available (for initial load precision)
    const apiDealValueBccy =
      parseFloat(projectItemCalculation.amountWithoutVatBccy || "0") || 0;
    const dealValue = parseFloat(projectItemCalculation.amountWithoutVat) || 0;
    const rate = currencyRate ? parseFloat(currencyRate) : 1;
    const calculatedDealValueBccy = Math.round(dealValue * rate * 100) / 100;

    // Use API value if it exists and no user edits, otherwise use calculated
    const dealValueBccy =
      apiDealValueBccy > 0 &&
      !changeFlags.isDiscountChanged &&
      !changeFlags.isDiscountValueChanged &&
      !changeFlags.isDealValueChanged
        ? apiDealValueBccy
        : calculatedDealValueBccy;

    if (dealValueBccy === 0) return 0;
    // Margin % = (Gross Profit BCCY / Deal Value BCCY) * 100
    return (grossProfitBccy / dealValueBccy) * 100;
  }, [
    grossProfitBccy,
    projectItemCalculation.amountWithoutVat,
    projectItemCalculation.amountWithoutVatBccy,
    currencyRate,
    changeFlags.isDiscountChanged,
    changeFlags.isDiscountValueChanged,
    changeFlags.isDealValueChanged,
  ]);

  const marginValueBccy = useMemo(() => {
    return grossProfitBccy;
  }, [grossProfitBccy]);

  const marginValue = useMemo(() => {
    return grossProfit;
  }, [grossProfit]);

  // Format number with thousands separator
  const formatCurrency = useCallback((value: number): string => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }, []);

  // Calculate bar width percentage (capped at 100%)
  const barWidthPercentage = useMemo(() => {
    const percentage = Math.abs(marginPercentage);
    return Math.min(percentage, 100);
  }, [marginPercentage]);

  // Discount calculations now handled in reducer

  /**
   * Returns an onKeyDown handler that fires `handler` when Enter is pressed.
   * Used on every editable NumericTextField so the user can confirm a change
   * without having to click the checkmark button.
   */
  const onEnterKey = useCallback(
    (handler: () => void) => (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handler();
    },
    [],
  );

  const handleSaveDealValue = useCallback(async () => {
    if (!selectedItem?.id) {
      ToastQueue.negative(
        t("no_project_selected", {
          ns: "layout_components",
          defaultValue: "No project selected",
        }),
        { timeout: 3000 },
      );
      return;
    }

    try {
      const res = await itemService.modifyProjectItemValues(
        new ModifyProjectItemValuesRequest(store.Server, store.SessionId, {
          projectId: selectedItem?.id?.toString() || "",
          billingStatus: 0,
          method: "SetSalesBySales",
          value: projectItemCalculation.amountWithoutVat,
        }),
      );

      if (res?.result === "OK") {
        ToastQueue.positive(
          t("deal_value_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Deal value updated successfully",
          }),
          {
            timeout: 3000,
          },
        );
        await Promise.all([
          loadProjectItemCalculation(String(selectedItem?.id)),
          loadProjectItemGridData(),
        ]);
        dispatch({ type: "SAVE_DEAL_VALUE" });
      } else {
        ToastQueue.negative(
          t("failed_to_update_deal_value", {
            ns: "layout_components",
            defaultValue: "Failed to update deal value",
          }),
          { timeout: 3000 },
        );
      }
    } catch (error) {
      console.error("Error saving deal value:", error);
      ToastQueue.negative(
        t("error_updating_deal_value", {
          ns: "layout_components",
          defaultValue: "Error updating deal value",
        }),
        { timeout: 3000 },
      );
    }
  }, [
    selectedItem?.id,
    itemService,
    store.Server,
    store.SessionId,
    projectItemCalculation,
    loadProjectItemCalculation,
    loadProjectItemGridData,
    t,
  ]);

  const handleCancelDealValue = useCallback(() => {
    dispatch({ type: "CANCEL_DEAL_VALUE" });
  }, []);

  const handleSavePurchaseCost = useCallback(async () => {
    if (!selectedItem?.id) {
      ToastQueue.negative(
        t("no_project_selected", {
          ns: "layout_components",
          defaultValue: "No project selected",
        }),
        { timeout: 3000 },
      );
      return;
    }

    try {
      const res = await itemService.modifyProjectItemValues(
        new ModifyProjectItemValuesRequest(store.Server, store.SessionId, {
          projectId: selectedItem?.id?.toString() || "",
          billingStatus: 0,
          method: "SetPurchaseByPurchase",
          value: projectItemCalculation.totalPurchase,
        }),
      );

      if (res?.result === "OK") {
        ToastQueue.positive(
          t("purchase_cost_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Purchase cost updated successfully",
          }),
          {
            timeout: 3000,
          },
        );
        await Promise.all([
          loadProjectItemCalculation(String(selectedItem?.id)),
          loadProjectItemGridData(),
        ]);
        dispatch({ type: "SAVE_PURCHASE_COST" });
      } else {
        ToastQueue.negative(
          t("failed_to_update_purchase_cost", {
            ns: "layout_components",
            defaultValue: "Failed to update purchase cost",
          }),
          { timeout: 3000 },
        );
      }
    } catch (error) {
      console.error("Error saving purchase cost:", error);
      ToastQueue.negative(
        t("error_updating_purchase_cost", {
          ns: "layout_components",
          defaultValue: "Error updating purchase cost",
        }),
        { timeout: 3000 },
      );
    }
  }, [
    selectedItem?.id,
    itemService,
    store.Server,
    store.SessionId,
    projectItemCalculation.totalPurchase,
    loadProjectItemCalculation,
    loadProjectItemGridData,
    t,
  ]);

  const handleCancelPurchaseCost = useCallback(() => {
    dispatch({ type: "CANCEL_PURCHASE_COST" });
  }, []);

  const handleSaveCatalogPrice = useCallback(async () => {
    try {
      const res = await itemService.modifyProjectItemValues(
        new ModifyProjectItemValuesRequest(store.Server, store.SessionId, {
          projectId: selectedItem?.id?.toString() || "",
          billingStatus: "0",
          method: "SetSalesByDiscount",
          value: discountValueInput,
        }),
      );

      if (res?.result === "OK") {
        ToastQueue.positive(
          t("discount_value_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Discount value updated successfully",
          }),
          {
            timeout: 3000,
          },
        );
        await Promise.all([
          loadProjectItemCalculation(String(selectedItem?.id)),
          loadProjectItemGridData(),
        ]);
        dispatch({ type: "SAVE_CATALOG_PRICE" });
      } else {
        ToastQueue.negative(
          t("failed_to_update_discount_value", {
            ns: "layout_components",
            defaultValue: "Failed to update discount value",
          }),
          { timeout: 3000 },
        );
      }
    } catch (error) {
      console.error("Error saving catalog price:", error);
      ToastQueue.negative(
        t("error_updating_discount_value", {
          ns: "layout_components",
          defaultValue: "Error updating discount value",
        }),
        { timeout: 3000 },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedItem?.id,
    itemService,
    store.Server,
    store.SessionId,
    discountValueInput,
    loadProjectItemCalculation,
    loadProjectItemGridData,
  ]);

  const handleCancelCatalogPrice = useCallback(() => {
    dispatch({ type: "CANCEL_CATALOG_PRICE" });
  }, []);

  const handleSaveMargin = useCallback(async () => {
    if (!selectedItem?.id) {
      ToastQueue.negative(
        t("no_project_selected", {
          ns: "layout_components",
          defaultValue: "No project selected",
        }),
        { timeout: 3000 },
      );
      return;
    }

    try {
      const res = await itemService.modifyProjectItemValues(
        new ModifyProjectItemValuesRequest(store.Server, store.SessionId, {
          projectId: selectedItem?.id?.toString() || "",
          billingStatus: 0,
          method: "SetSalesByPurchase",
          value: projectItemCalculation.amountWithoutVat,
        }),
      );

      if (res?.result === "OK") {
        ToastQueue.positive(
          t("margin_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Margin updated successfully",
          }),
          { timeout: 3000 },
        );
        await Promise.all([
          loadProjectItemCalculation(String(selectedItem?.id)),
          loadProjectItemGridData(),
        ]);
        dispatch({ type: "SAVE_MARGIN" });
      } else {
        ToastQueue.negative(
          t("failed_to_update_margin", {
            ns: "layout_components",
            defaultValue: "Failed to update margin",
          }),
          { timeout: 3000 },
        );
      }
    } catch (error) {
      console.error("Error saving margin:", error);
      ToastQueue.negative(
        t("error_updating_margin", {
          ns: "layout_components",
          defaultValue: "Error updating margin",
        }),
        { timeout: 3000 },
      );
    }
  }, [
    selectedItem?.id,
    itemService,
    store.Server,
    store.SessionId,
    projectItemCalculation,
    loadProjectItemCalculation,
    loadProjectItemGridData,
    t,
  ]);

  const handleCancelMargin = useCallback(() => {
    dispatch({ type: "CANCEL_MARGIN" });
  }, []);

  const handleDeleteRow = useCallback(
    async (rowId: string) => {
      // Prevent double-deletion
      if (isDeleting.current) {
        return;
      }

      if (!selectedItem?.id) {
        ToastQueue.negative(
          t("no_project_selected", {
            ns: "layout_components",
            defaultValue: "No project selected",
          }),
          { timeout: 3000 },
        );
        return;
      }

      try {
        isDeleting.current = true;
        setShowLoader(true);

        const result = await itemService.deleteProjectItem(
          new DeleteProjectItemRequest(store.Server, store.SessionId, rowId),
        );

        if (result.result === "OK") {
          // Clear selection for the deleted row
          dispatchSelection({
            type: "SET_SELECTED_IDS",
            selectedIds: selectedIds.filter((id) => id !== rowId),
          });

          // Remove the deleted row from local state immediately – no full reload
          setRowsData((prev: any) =>
            prev.filter((row: any) => row.id !== rowId),
          );

          // Refresh header calculation and project details
          await Promise.all([
            loadProjectItemCalculation(String(selectedItem.id)),
            loadProjectDetails(selectedItem.id as number),
          ]);

          ToastQueue.positive(
            t("record_deleted_successfully", {
              ns: "layout_components",
              defaultValue: "Record deleted successfully",
            }),
            { timeout: 3000 },
          );
        } else {
          ToastQueue.negative(
            t("error_deleting_record", {
              ns: "layout_components",
              defaultValue: "Error deleting record",
            }),
            { timeout: 3000 },
          );
        }
      } catch (error) {
        console.error("Error deleting row:", error);
      } finally {
        isDeleting.current = false;
        setShowLoader(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedItem?.id,
      itemService,
      store.Server,
      store.SessionId,
      loadProjectItemCalculation,
      selectedIds,
      dispatchSelection,
    ],
  );

  // Handle Add Item button click - adds a new row at the end of the grid
  const handleAddItemClick = useCallback(() => {
    // Create a unique ID for the new row
    const newRowId = `new-${Date.now()}`;

    // Create a new row and add it to rowsData in edit mode
    const newRowData = {
      id: newRowId,
      isNew: true,
      productName: "",
      name: "",
      description: "",
      quantity: 1,
      unitPrice: 0,
      totalPriceCurr: "0 " + (projectItemCalculation.currency || bccyCurrency),
      unitPriceCurr: "0 " + (projectItemCalculation.currency || bccyCurrency),
      totalAmountWithVatCurr:
        "0 " + (projectItemCalculation.currency || bccyCurrency),
      currency: projectItemCalculation.currency || bccyCurrency,
      vatValue: 0,
      vatPercent: "0%",
      amountWithVat: 0,
      purchasePrice: 0,
      partID: "",
      refId: "",
    };

    // Add the new row to the rowsData
    setRowsData((prev) => [...prev, newRowData]);

    // Set the new row ID for auto-edit mode
    setEditingNewRowId(newRowId);
  }, [projectItemCalculation.currency, bccyCurrency]);

  // Handle edit cancel - remove new row if it was never saved
  const handleEditCancel = useCallback((rowId: string) => {
    //console.log("[ProjectItemGrid] handleEditCancel called with rowId:", rowId);
    // Check if this is a new row (starts with "new-" or "row_" prefix)
    if (rowId.startsWith("new-") || rowId.startsWith("row_")) {
      //console.log("[ProjectItemGrid] Removing new row:", rowId);
      // Remove the unsaved new row from rowsData
      setRowsData((prev) => prev.filter((item) => item.id !== rowId));
    }
    // Clear the editing new row ID
    setEditingNewRowId(null);
  }, []);

  const updateRecord = useCallback(
    async (record: any) => {
      try {
        //console.log("record", record);
        //console.log("rowsData", rowsData);
        //eslint-disable-next-line
        let itemRow = rowsData.find((row: any) => row.id == record.id);
        //console.log("updatedRowData", itemRow);
        if (!itemRow) {
          console.error("Item row not found in rowsData for id:", record.id);
          ToastQueue.negative(
            t("error_item_data_not_found", {
              ns: "layout_components",
              defaultValue: "Error: Item data not found",
            }),
            { timeout: 3000 },
          );
          return;
        }

        //console.log('update record', record);
        setRecordIds(record, selectedItem);

        /** remove % from ratio  */
        const ratioValue =
          itemRow.ratioValue ||
          (itemRow.ratio
            ? itemRow.ratio.toString().replace("%", "").trim()
            : "0");

        // Recalculate totals from current editable values so the server always
        // receives consistent figures regardless of which field was edited.
        const _qty = parseFloat(String(record.quantity)) || 1;
        const _unitPrice = parseFloat(String(record.unitPrice)) || 0;
        const _vatValue = parseFloat(String(record.vatValue)) || 0;
        const _calculatedTotalPrice = _qty * _unitPrice;
        const _calculatedTotalWithVat =
          _calculatedTotalPrice * (1 + _vatValue / 100);

        let result = await itemService.updateProjectItem(
          new UpdateProjectItemRequest(
            store.Server,
            store.SessionId,
            generateProjectProductItem(
              record.folderId,
              record.projectId,
              record.activityId,
              record.partID,
              record.name || record.productName,
              record.quantity,
              record.unitPrice,
              _calculatedTotalPrice,
              _calculatedTotalWithVat,
              record.currency,
              record.vatValue || 0,
              record.purchasePrice,
              record.attrib0,
              record.attrib0_id,
              record.attrib1,
              record.attrib1_id,
              record.attrib2,
              record.attrib2_id,
              record.attrib3,
              record.attrib3_id,
              record.attrib4,
              record.attrib4_id,
              record.attrib5,
              record.attrib5_id,
              record.attrib6,
              record.attrib6_id,
              record.attrib7,
              record.attrib7_id,
              record.attrib8,
              record.attrib8_id,
              record.attrib9,
              record.attrib9_id,
              record.supplier,
              record.refId,
              record.billingDate,
              record.billing_status || "0",
              record.parentId ? String(record.parentId) : undefined,
              { ...itemRow, ratio: ratioValue },
            ),
          ),
        );

        if (result.result === "OK") {
          ToastQueue.positive(
            t("record_updated_successfully", { ns: "layout_components" }),
            { timeout: 3000 },
          );
          // Update only the changed row in local state – no full grid reload to
          // preserve focus/scroll position during inline editing.
          const _currency = record.currency || itemRow.currency || "";
          const formatPriceLocal = (
            value: number | string,
            currency: string,
          ): string => {
            const numValue =
              typeof value === "string" ? parseFloat(value) : value;
            if (isNaN(numValue)) return `0 ${currency}`;
            return `${numValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency}`;
          };
          setRowsData((prev: any) =>
            prev.map((row: any) =>
              // eslint-disable-next-line
              row.id == record.id
                ? {
                    ...row,
                    name: record.name || record.productName,
                    productName: record.name || record.productName,
                    description: record.description,
                    quantity: formatDecimalToString(
                      parseFloat(String(record.quantity || 1)).toFixed(2),
                    ),
                    unitPrice: formatDecimalToString(String(record.unitPrice)),
                    unitPriceCurr: formatPriceLocal(
                      record.unitPrice,
                      _currency,
                    ),
                    totalPrice: formatDecimalToString(
                      _calculatedTotalPrice.toFixed(2),
                    ),
                    totalPriceCurr: formatPriceLocal(
                      _calculatedTotalPrice,
                      _currency,
                    ),
                    totalAmountWithVat: _calculatedTotalWithVat,
                    totalAmountWithVatCurr: formatPriceLocal(
                      _calculatedTotalWithVat,
                      _currency,
                    ),
                    vatValue: _vatValue,
                    vatPercent: `${_vatValue}%`,
                    purchasePrice: record.purchasePrice,
                    currency: _currency,
                    currencyRate: record.currencyRate,
                    supplier: record.supplier,
                    supplierName: record.supplierName,
                    billingDate: record.billingDate,
                    billingDateFormatted: moment(record.billingDate).format(
                      "MM/DD/YYYY",
                    ),
                    refId: record.refId,
                    attrib0: record.attrib0,
                    attrib1: record.attrib1,
                    attrib2: record.attrib2,
                    attrib3: record.attrib3,
                    attrib4: record.attrib4,
                    attrib5: record.attrib5,
                    attrib6: record.attrib6,
                    attrib7: record.attrib7,
                    attrib8: record.attrib8,
                    attrib9: record.attrib9,
                  }
                : row,
            ),
          );
          // Refresh header calculation totals only – grid rows stay intact
          if (selectedItem?.id) {
            await loadProjectItemCalculation(String(selectedItem.id));
          }
        } else {
          ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
        }
      } catch (error) {
        console.error("Error", error);
        throw error;
      }
    },
    [
      rowsData,
      selectedItem,
      itemService,
      store.Server,
      store.SessionId,
      t,
      loadProjectItemCalculation,
    ],
  );

  // Handle tree grid row create callback (called when user presses Ctrl/Cmd+Enter)
  // Note: TreeGrid creates the row internally, this is just a notification callback
  const handleTreeGridRowCreate = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (parentId: string | null, rowType: "simple" | "product") => {
      // TreeGrid already creates the row with "row_" prefix internally
      // We just need to be aware it was created - no action needed here
    },
    [],
  );

  // Handle row move (indent / outdent) - persists the new parentId to the server
  const handleRowMove = useCallback(
    async (rowId: string, newParentId: string | null) => {
      const itemRow = rowsData.find((row: any) => row.id === rowId);
      if (!itemRow) {
        console.error("handleRowMove: row not found", rowId);
        return;
      }

      // Update local state immediately for responsive UI
      setRowsData((prev) =>
        prev.map((row: any) =>
          row.id === rowId ? { ...row, parentId: newParentId } : row,
        ),
      );

      try {
        const record: any = { ...itemRow };
        setRecordIds(record, selectedItem);

        const ratioValue =
          itemRow.ratioValue ||
          (itemRow.ratio
            ? itemRow.ratio.toString().replace("%", "").trim()
            : "0");

        const result = await itemService.updateProjectItem(
          new UpdateProjectItemRequest(
            store.Server,
            store.SessionId,
            generateProjectProductItem(
              record.folderId,
              record.projectId,
              record.activityId,
              itemRow.partID,
              itemRow.name || itemRow.productName,
              itemRow.quantity,
              itemRow.unitPrice,
              itemRow.totalPrice,
              itemRow.totalAmountWithVat || 0,
              itemRow.currency,
              itemRow.vatValue || 0,
              itemRow.purchasePrice,
              itemRow.attrib0,
              itemRow.attrib0_id,
              itemRow.attrib1,
              itemRow.attrib1_id,
              itemRow.attrib2,
              itemRow.attrib2_id,
              itemRow.attrib3,
              itemRow.attrib3_id,
              itemRow.attrib4,
              itemRow.attrib4_id,
              itemRow.attrib5,
              itemRow.attrib5_id,
              itemRow.attrib6,
              itemRow.attrib6_id,
              itemRow.attrib7,
              itemRow.attrib7_id,
              itemRow.attrib8,
              itemRow.attrib8_id,
              itemRow.attrib9,
              itemRow.attrib9_id,
              itemRow.supplier,
              itemRow.refId,
              itemRow.billingDate,
              itemRow.billing_status || "0",
              newParentId || undefined,
              { ...itemRow, ratio: ratioValue },
            ),
          ),
        );

        if (result.result !== "OK") {
          ToastQueue.negative(
            result.EXCEPTION?.reason || "Failed to update item parent",
            { timeout: 3000 },
          );
          // Revert local state on failure
          setRowsData((prev) =>
            prev.map((row: any) =>
              row.id === rowId ? { ...row, parentId: itemRow.parentId } : row,
            ),
          );
        }
      } catch (error) {
        console.error("Error updating item parentId:", error);
        ToastQueue.negative(
          t("error_updating_item_parent", {
            ns: "layout_components",
            defaultValue: "Error updating item parent",
          }),
          { timeout: 3000 },
        );
        // Revert local state on failure
        setRowsData((prev) =>
          prev.map((row: any) =>
            row.id === rowId ? { ...row, parentId: itemRow.parentId } : row,
          ),
        );
      }
    },
    [rowsData, selectedItem, itemService, store.Server, store.SessionId, t],
  );

  // Handle row update - called when a row edit is committed
  const handleTreeGridRowUpdate = useCallback(
    async (row: TreeGridRow) => {
      const data = row.data;
      // Check for both "new-" (Add Item button) and "row_" (Cmd+Enter) prefixes
      const isNew =
        data.isNew || row.id.startsWith("new-") || row.id.startsWith("row_");

      if (isNew) {
        // Clear the editing new row ID since it's being saved
        setEditingNewRowId(null);

        // Save new item
        if (!selectedItem?.id) {
          ToastQueue.negative(
            t("no_project_selected", {
              ns: "layout_components",
              defaultValue: "No project selected",
            }),
            { timeout: 3000 },
          );
          return;
        }

        if (!data.productName && !data.partID) {
          ToastQueue.negative(
            t("please_select_a_product", {
              ns: "layout_components",
              defaultValue: "Please select a product",
            }),
            { timeout: 3000 },
          );
          return;
        }

        try {
          const record: any = {};
          setRecordIds(record, selectedItem);

          const itemName = data.name || data.productName;
          const quantity = parseFloat(data.quantity) || 1;
          const unitPrice = parseFloat(data.unitPrice) || 0;
          const totalPrice = quantity * unitPrice;
          const amountWithVat = parseFloat(data.amountWithVat) || 0;

          const result = await itemService.createProjectItem(
            new CreateProjectItemRequest(
              store.Server,
              store.SessionId,
              generateProjectProductItem(
                record.folderId || "",
                record.projectId || "",
                record.activityId || "",
                data.partID || "",
                itemName,
                quantity,
                unitPrice,
                totalPrice,
                amountWithVat,
                data.currency || "",
                String(data.vatValue || 0),
                String(data.purchasePrice || 0),
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                data.refId || "",
                new Date().toISOString().split("T")[0],
                "0",
                row.parentId ? String(row.parentId) : undefined,
                {
                  type: "0",
                  ratio:
                    record.ratioValue || record.ratio?.replace("%", "") || "0",
                },
              ),
            ),
          );

          if (result.result === "OK") {
            ToastQueue.positive(
              t("item_added_successfully", {
                ns: "layout_components",
                defaultValue: "Item added successfully",
              }),
              { timeout: 3000 },
            );
            // Refresh data
            await Promise.all([
              loadProjectItemCalculation(String(selectedItem.id)),
              loadProjectItemGridData(),
            ]);
          } else {
            ToastQueue.negative(
              result.EXCEPTION?.reason ||
                t("failed_to_add_item", {
                  ns: "layout_components",
                  defaultValue: "Failed to add item",
                }),
              {
                timeout: 3000,
              },
            );
          }
        } catch (error) {
          console.error("Error adding item:", error);
          ToastQueue.negative(
            t("error_adding_item", {
              ns: "layout_components",
              defaultValue: "Error adding item",
            }),
            { timeout: 3000 },
          );
        }
      } else {
        // Update existing item
        //console.log("data", data);
        await updateRecord(data);
      }
    },
    [
      selectedItem,
      itemService,
      store.Server,
      store.SessionId,
      loadProjectItemCalculation,
      loadProjectItemGridData,
      updateRecord,
      t,
    ],
  );

  // Ref to prevent double-submission for multi-add
  const isSavingMultiProducts = useRef<boolean>(false);

  // Handle multi-product selection from ProductSelectionModal
  const handleMultiProductSave = useCallback(
    async (selectedProducts: SelectedProduct[]) => {
      // Prevent double-submission
      if (isSavingMultiProducts.current) {
        return;
      }

      // Validation
      if (!selectedProducts || selectedProducts.length === 0) {
        ToastQueue.negative(
          t("no_products_selected", {
            ns: "layout_components",
            defaultValue: "No products selected",
          }),
          { timeout: 3000 },
        );
        return;
      }

      if (!selectedItem?.id) {
        ToastQueue.negative(
          t("no_project_selected", {
            ns: "layout_components",
            defaultValue: "No project selected",
          }),
          { timeout: 3000 },
        );
        return;
      }

      isSavingMultiProducts.current = true;
      setShowLoader(true);

      // Track success/failure counts
      let successCount = 0;
      let failureCount = 0;
      const failedProducts: string[] = [];

      try {
        // Set record IDs once (reusable for all products)
        const record: any = {};
        setRecordIds(record, selectedItem);

        const billingDate = new Date().toISOString().split("T")[0];

        // Process all products in parallel for better performance
        const productPromises = selectedProducts.map(async (product) => {
          try {
            // console.log(
            //   "Processing product:",
            //   product.productName,
            //   "Components:",
            //   product.components,
            // );
            const quantity = product.quantity || 1;
            const unitPrice = product.unitPrice || 0;
            const totalPrice = quantity * unitPrice;

            // Create main product
            const result = await itemService.createProjectItem(
              new CreateProjectItemRequest(
                store.Server,
                store.SessionId,
                generateProjectProductItem(
                  record.folderId || "",
                  record.projectId || "",
                  record.activityId || "",
                  product.partID || "",
                  product.productName,
                  quantity,
                  unitPrice,
                  totalPrice,
                  product.amountWithVat || 0,
                  product.currency || "",
                  String(product.vatValue || 0),
                  String(product.purchasePrice || 0),
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  product.partNumber || "",
                  billingDate,
                  "0",
                  undefined,
                  { type: "0", ratio: "100" },
                ),
              ),
            );

            if (result.result !== "OK") {
              throw new Error(
                result.EXCEPTION?.reason || "Failed to create product",
              );
            }

            // Save component products if they exist (sequentially to maintain order)
            // Get the parent item ID from the result to link components
            const parentItemId = result.itemId;
            // console.log(
            //   "Checking components for product:",
            //   product.productName,
            //   "Has components:",
            //   !!product.components,
            //   "Length:",
            //   product.components?.length,
            //   "Parent itemId:",
            //   parentItemId,
            // );
            if (product.components && product.components.length > 0) {
              // console.log(
              //   "Saving",
              //   product.components.length,
              //   "components for product:",
              //   product.productName,
              // );
              for (const component of product.components) {
                // console.log(
                //   "Saving component:",
                //   component.componentName,
                //   component,
                // );
                try {
                  const compQuantity = parseFloat(component.countMin) || 1;
                  const compUnitPrice = parseFloat(component.amount) || 0;
                  const compTotalPrice = compUnitPrice * compQuantity;
                  const compAmountWithVat =
                    (parseFloat(component.amountWithVat) || 0) * compQuantity;

                  await itemService.createProjectItem(
                    new CreateProjectItemRequest(
                      store.Server,
                      store.SessionId,
                      generateProjectProductItem(
                        record.folderId || "",
                        record.projectId || "",
                        record.activityId || "",
                        component.component,
                        component.componentName,
                        compQuantity,
                        compUnitPrice,
                        compTotalPrice,
                        compAmountWithVat,
                        component.currencyName || "",
                        String(component.vatValue || 0),
                        String(component.purchasePrice || 0),
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        "",
                        billingDate,
                        "0",
                        parentItemId,
                        { type: "0", ratio: "100" },
                      ),
                    ),
                  );
                  //console.log("Component save result:", compResult);
                } catch (compError) {
                  console.error(
                    `Error saving component "${component.componentName}":`,
                    compError,
                  );
                  // Continue with other components even if one fails
                }
              }
            }

            successCount++;
          } catch (productError) {
            failureCount++;
            failedProducts.push(product.productName);
            console.error(
              `Error adding product "${product.productName}":`,
              productError,
            );
          }
        });

        // Wait for all products to be processed
        await Promise.all(productPromises);

        // Show appropriate feedback
        if (successCount > 0 && failureCount === 0) {
          ToastQueue.positive(
            `${successCount} ${t("item(s)_added_successfully", {
              ns: "layout_components",
              defaultValue: "item(s) added successfully",
            })}`,
            {
              timeout: 3000,
            },
          );
        } else if (successCount > 0 && failureCount > 0) {
          ToastQueue.info(
            `${successCount} ${t("item(s)_added", {
              ns: "layout_components",
              defaultValue: "item(s) added",
            })}, ${failureCount} ${t("failed", {
              ns: "layout_components",
              defaultValue: "failed",
            })}: ${failedProducts.join(", ")}`,
            { timeout: 5000 },
          );
        } else {
          ToastQueue.negative(
            `${t("failed_to_add_items", {
              ns: "layout_components",
              defaultValue: "Failed to add items",
            })}: ${failedProducts.join(", ")}`,
            { timeout: 5000 },
          );
        }

        setIsProductSelectionModalOpen(false);

        // Refresh data
        await Promise.all([
          loadProjectItemCalculation(String(selectedItem.id)),
          loadProjectItemGridData(),
        ]);
      } catch (error) {
        console.error("Error in multi-product save:", error);
        ToastQueue.negative(
          t("error_adding_items", {
            ns: "layout_components",
            defaultValue: "Error adding items",
          }),
          { timeout: 3000 },
        );
      } finally {
        isSavingMultiProducts.current = false;
        setShowLoader(false);
      }
    },
    [
      selectedItem,
      itemService,
      store.Server,
      store.SessionId,
      loadProjectItemCalculation,
      loadProjectItemGridData,
      t,
    ],
  );

  // Matches the InvoiceWrapper pattern: accepts the full invoice object and the
  // documentType string (e.g. "invoice-issued") so the correct app can be opened.
  const handleOpenInvoiceApp = (item: any, documentType: string) => {
    if (!item?.id) return;
    const docTypeToInvoiceType: Record<string, InvoiceType> = {
      "offer-issued": InvoiceType.OFFER_ISSUED,
      "order-received": InvoiceType.ORDER_RECEIVED,
      "invoice-issued": InvoiceType.INVOICE_ISSUED,
      "offer-received": InvoiceType.OFFER_RECEIVED,
      "order-issued": InvoiceType.ORDER_ISSUED,
      "invoice-received": InvoiceType.INVOICE_RECEIVED,
    };
    const invoiceAppIdent = mapInvoiceTypeToAppIdent(
      docTypeToInvoiceType[documentType] ?? InvoiceType.INVOICE_ISSUED,
    );
    invokeInvoiceApp(String(item.id), invoiceAppIdent);
  };

  // Generic handler for creating invoice documents
  const createInvoiceDocument = useCallback(
    async (documentType: string, documentName: string) => {
      if (!selectedItem) {
        ToastQueue.negative(
          t("no_project_selected", {
            ns: "layout_components",
            defaultValue: "No project selected",
          }),
          { timeout: 3000 },
        );
        return;
      }
      //console.log("selectedIds", selectedIds.length);
      setShowLoader(true);
      try {
        // Build request options
        const requestOptions: any = {
          PROJECTS: [{ id: selectedItem.id.toString() }],
          DOCUMENTTYPE: documentType,
          SOURCETYPE: "sales",
        };

        // If items are selected, include them in the request
        if (selectedIds.length > 0) {
          requestOptions.ITEMS = selectedIds.map((id) => ({ id }));
        }
        //console.log("requestOptions", requestOptions);

        const response: any = await invoiceService.createInvoicesFromItems(
          new CreateInvoicesFromItemsRequest(
            store.Server,
            store.SessionId,
            requestOptions,
          ),
        );

        if (response?.result === "OK") {
          const invoices: any[] = [];
          if (response.INVOICE) {
            if (Array.isArray(response.INVOICE)) {
              invoices.push(...response.INVOICE);
            } else {
              invoices.push(response.INVOICE);
            }
          }
          //console.log("CreateInvoicesFromItems response invoices:", invoices);
          if (invoices.length > 0) {
            // Show toast with action button for each created invoice
            invoices.forEach((invoice: any, index: number) => {
              // Normalise the id field — the API response uses different names
              const normalizedInvoice = {
                ...invoice,
                id: invoice.id ?? invoice.ID ?? invoice.invoiceId,
              };
              // console.log(
              //   `Creating toast for ${documentName} ${index + 1}, ID:`,
              //   normalizedInvoice.id,
              //   "Invoice Number:",
              //   normalizedInvoice.invoiceNumber,
              // );
              // Capture the invoice ID in a local variable to avoid closure issues
              const invoiceIdToOpen = normalizedInvoice.id;
              ToastQueue.positive(
                `${documentName} ${normalizedInvoice.invoiceNumber || index + 1} ${t(
                  "created_successfully",
                  {
                    ns: "layout_components",
                    defaultValue: "created successfully",
                  },
                )}`,
                {
                  timeout: 5000,
                  actionLabel: t("open", {
                    ns: "layout_components",
                    defaultValue: "Open",
                  }),
                  onAction: () => {
                    // console.log(
                    //   `Toast clicked - opening ${documentName} with ID:`,
                    //   invoiceIdToOpen,
                    // );
                    handleOpenInvoiceApp({ id: invoiceIdToOpen }, documentType);
                  },
                },
              );
            });
          } else {
            ToastQueue.negative(
              t("no_document_created", {
                ns: "layout_components",
                defaultValue: "No document created",
              }),
              { timeout: 3000 },
            );
          }
        } else {
          ToastQueue.negative(
            t("failed_to_create_document", {
              ns: "layout_components",
              defaultValue: `Failed to create ${documentName.toLowerCase()}`,
            }),
            { timeout: 3000 },
          );
        }
      } catch (error) {
        console.error(`Error creating ${documentName.toLowerCase()}:`, error);
        ToastQueue.negative(
          t("error_creating_document", {
            ns: "layout_components",
            defaultValue: `Error creating ${documentName.toLowerCase()}`,
          }),
          {
            timeout: 3000,
          },
        );
      } finally {
        //clear selected  SET_SELECTED_IDS
        dispatchSelection({ type: "SET_SELECTED_IDS", selectedIds: [] });
        setShowLoader(false);
      }
    },
    [selectedItem, invoiceService, store, selectedIds, t],
  );

  const handleCreateEstimate = useCallback(() => {
    createInvoiceDocument("offer-issued", "Estimate");
  }, [createInvoiceDocument]);

  const handleCreateSalesOrder = useCallback(() => {
    createInvoiceDocument("order-received", "Sales order");
  }, [createInvoiceDocument]);

  const handleCreateInvoice = useCallback(() => {
    createInvoiceDocument("invoice-issued", "Invoice");
  }, [createInvoiceDocument]);

  // Fetch the fpaTypeCategory register key for the current item type (used when opening Create Deal/Device/Project dialogs)
  useEffect(() => {
    const fetchProjectListKey = async () => {
      if (!selectedItem?.item_type_id) return;
      try {
        const resListRegister = await featureService.listRegister(
          new ListRegisterRequest(
            store.Server,
            store.SessionId,
            undefined,
            undefined,
            undefined,
            undefined,
            Number(selectedItem.item_type_id),
          ),
        );
        projectListKeyRef.current =
          resListRegister?.ROWS?.find(
            (row: any) => row?.ROW?.key === "FPATypeCategoryForListProject",
          )?.ROW?.value ?? "";
      } catch (e) {
        console.error("Error fetching project list key for ProjectItemGrid", e);
      }
    };
    fetchProjectListKey();
  }, [
    selectedItem?.item_type_id,
    featureService,
    store.Server,
    store.SessionId,
  ]);

  /** Returns selected rows if any are checked, otherwise all rows.
   * Uses String() coercion so that number/string ID mismatch between
   * the grid's selectionState (string[]) and rowsData does not silently
   * produce an empty list. Falls back to all rows if the filter yields
   * nothing (safety net for unexpected ID format differences). */
  const getItemsToTransfer = useCallback((): any[] => {
    if (selectedIds.length > 0) {
      const selectedSet = new Set(selectedIds.map(String));
      const filtered = rowsData.filter((r: any) =>
        selectedSet.has(String(r.id)),
      );
      // Fallback: if coercion still yields nothing, return all rows so the
      // user's intent ("transfer items") is always respected.
      return filtered.length > 0 ? filtered : rowsData;
    }
    return rowsData;
  }, [selectedIds, rowsData]);

  const handleCreateDeal = useCallback(() => {
    setCreateProjectDialogTarget({
      itemTypeTarget: "salesItems",
      items: getItemsToTransfer(),
    });
  }, [getItemsToTransfer]);

  const handleCreateDevice = useCallback(() => {
    setCreateProjectDialogTarget({
      itemTypeTarget: "technologyItems",
      items: getItemsToTransfer(),
    });
  }, [getItemsToTransfer]);

  const handleCreateProject = useCallback(() => {
    setCreateProjectDialogTarget({
      itemTypeTarget: "billingItems",
      items: getItemsToTransfer(),
    });
  }, [getItemsToTransfer]);

  const getProject = useCallback(
    async (projectId: number): Promise<any> => {
      try {
        const projectResponse = await projectService.getProject(
          new GetProjectRequest(store.Server, store.SessionId, projectId),
        );

        if (projectResponse.EXCEPTION) {
          console.error(
            "projectResponse.EXCEPTION " + projectResponse.EXCEPTION,
          );
          return null;
        }

        return projectResponse.PROJECT;
      } catch (error) {
        console.error("Error fetching project:", error);
        return null;
      }
    },
    [projectService, store.Server, store.SessionId],
  );

  const loadProjectDetails = useCallback(
    async (projectId: number) => {
      try {
        const project = await getProject(projectId);
        if (project) {
          // Set provider if exists
          if (project.provider) {
            setSelectedProviderId(project.provider);
            setProviderInputValue(project.providerName || "");
            providerList.setFilterText(project.providerName || "");
          }
          // Set invoice to (customer) if exists
          if (project.customer) {
            setSelectedInvoiceToId(project.customer);
            setInvoiceToInputValue(project.customerName || "");
            invoiceToList.setFilterText(project.customerName || "");
          }
          // Update expected close date if exists
          if (project.expectedCloseDate) {
            const formattedDate = moment(project.expectedCloseDate).format(
              "YYYY-MM-DD",
            );
            dispatch({ type: "UPDATE_FINAL_DATE", date: formattedDate });
            setPendingExpectedCloseDate(formattedDate);
          }
        }
      } catch (error) {
        console.error("Error loading project details:", error);
      }
    },
    [getProject, providerList, invoiceToList],
  );

  // Main data loading effect - must be after all function definitions
  useEffect(() => {
    (async () => {
      if (!selectedItem?.id) {
        setShowLoader(false);
        return;
      }

      setShowLoader(true);
      try {
        await Promise.all([
          loadProjectItemGridData(),
          loadProjectItemCalculation(String(selectedItem.id)),
          loadProjectDetails(selectedItem.id as number),
        ]);
      } catch (error) {
        console.error("Error loading project data:", error);
      } finally {
        setShowLoader(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, reloadComponent]);

  const updateProjectProvider = useCallback(
    async (providerId: any): Promise<boolean> => {
      if (!selectedItem?.id) {
        console.error("No selected item ID available");
        ToastQueue.negative(
          t("no_project_selected", {
            ns: "layout_components",
            defaultValue: "No project selected",
          }),
          { timeout: 3000 },
        );
        return false;
      }

      try {
        const project = await getProject(selectedItem.id as number);
        if (!project) {
          ToastQueue.negative(
            t("failed_to_load_project_details", {
              ns: "layout_components",
              defaultValue: "Failed to load project details",
            }),
            { timeout: 3000 },
          );
          return false;
        }

        const update = await projectService.updateProject(
          new UpdateProjectRequest(store.Server, store.SessionId, {
            ...project,
            provider: providerId,
          }),
        );

        if (update.EXCEPTION) {
          console.error("update.EXCEPTION " + update.EXCEPTION);
          ToastQueue.negative(
            t("failed_to_update_provider", {
              ns: "layout_components",
              defaultValue: "Failed to update provider",
            }),
            { timeout: 3000 },
          );
          return false;
        }

        ToastQueue.positive(
          t("provider_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Provider updated successfully",
          }),
          { timeout: 2000 },
        );
        return true;
      } catch (error) {
        console.error("Error updating provider:", error);
        ToastQueue.negative(
          t("error_updating_provider", {
            ns: "layout_components",
            defaultValue: "Error updating provider",
          }),
          { timeout: 3000 },
        );
        return false;
      }
    },
    [
      selectedItem?.id,
      getProject,
      projectService,
      store.Server,
      store.SessionId,
      t,
    ],
  );

  const updateProjectInvoiceTo = useCallback(
    async (invoiceToId: any): Promise<boolean> => {
      if (!selectedItem?.id) {
        console.error("No selected item ID available");
        ToastQueue.negative(
          t("no_project_selected", {
            ns: "layout_components",
            defaultValue: "No project selected",
          }),
          { timeout: 3000 },
        );
        return false;
      }

      try {
        const project = await getProject(selectedItem.id as number);
        if (!project) {
          ToastQueue.negative(
            t("failed_to_load_project_details", {
              ns: "layout_components",
              defaultValue: "Failed to load project details",
            }),
            { timeout: 3000 },
          );
          return false;
        }

        const update = await projectService.updateProject(
          new UpdateProjectRequest(store.Server, store.SessionId, {
            ...project,
            customer: invoiceToId,
          }),
        );

        if (update.EXCEPTION) {
          console.error("update.EXCEPTION " + update.EXCEPTION);
          ToastQueue.negative(
            t("failed_to_update_invoice_recipient", {
              ns: "layout_components",
              defaultValue: "Failed to update invoice recipient",
            }),
            { timeout: 3000 },
          );
          return false;
        }

        ToastQueue.positive(
          t("invoice_recipient_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Invoice recipient updated successfully",
          }),
          { timeout: 2000 },
        );
        return true;
      } catch (error) {
        console.error("Error updating invoice recipient:", error);
        ToastQueue.negative(
          t("error_updating_invoice_recipient", {
            ns: "layout_components",
            defaultValue: "Error updating invoice recipient",
          }),
          { timeout: 3000 },
        );
        return false;
      }
    },
    [
      selectedItem?.id,
      getProject,
      projectService,
      store.Server,
      store.SessionId,
      t,
    ],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateProjectExpectedCloseDate = useCallback(
    async (newDate: string, updateExchangeRate: boolean): Promise<boolean> => {
      try {
        if (
          updateExchangeRate &&
          projectItemCalculation.currency !== bccyCurrency
        ) {
          const currency_id = currencies.find(
            (cur: any) => cur.code === projectItemCalculation.currency,
          )?.id;

          if (currency_id) {
            await getCurrencyRate(currency_id, newDate + "T00:00:00");
          }
        }

        dispatch({ type: "UPDATE_FINAL_DATE", date: newDate });
        return true;
      } catch (error) {
        console.error("Error updating expected close date:", error);
        ToastQueue.negative(
          t("failed_to_update_expected_close_date", {
            ns: "layout_components",
            defaultValue: "Failed to update expected close date",
          }),
          { timeout: 3000 },
        );
        return false;
      }
    },
    [projectItemCalculation, currencies, getCurrencyRate, t, bccyCurrency],
  );

  const updateExchangeRate = useCallback(
    async (updateFinal: boolean, overrideRate?: string): Promise<boolean> => {
      if (!selectedItem?.id) {
        ToastQueue.negative(
          t("no_project_selected", {
            ns: "layout_components",
            defaultValue: "No project selected",
          }),
          { timeout: 3000 },
        );
        return false;
      }

      const rateToUse = overrideRate ?? tempRate;
      //console.log('Using rate:', rateToUse, 'Override:', overrideRate, 'tempRate:', tempRate);

      try {
        setShowLoader(true);
        // Update project item calculation
        const resProjectItem = await itemService.updateProjectItemCalculation(
          new UpdateProjectItemCalculationRequest(
            store.Server,
            store.SessionId,
            projectItemCalculation?.id?.toString() || "",
            [
              {
                ...projectItemCalculation,
                ...(updateFinal && { finalDate: pendingExpectedCloseDate }),
                projectId: selectedItem.id.toString(),
                currency: tempCurrency,
                modified: new Date(),
              } as any,
            ],
          ),
        );

        // Check if main update was successful
        if (resProjectItem?.result !== "OK") {
          ToastQueue.negative(
            t("failed_to_update_exchange_rate", {
              ns: "layout_components",
              defaultValue: "Failed to update exchange rate",
            }),
            { timeout: 3000 },
          );
          return false;
        }

        // Validate project raw data exists
        if (!projectRawData.current || !projectRawData.current.ROWS) {
          console.error("Project raw data not loaded");
          ToastQueue.negative(
            t("project_data_not_loaded", {
              ns: "layout_components",
              defaultValue: "Project data not loaded. Please refresh.",
            }),
            { timeout: 3000 },
          );
          return false;
        }

        // Conversion factor: prices are stored in the old currency.
        // To get the BCCY value: price_old_ccy * oldRate
        // To get the new currency value: BCCY_value / newRate
        // Combined: price_new_ccy = price_old_ccy * (oldRate / newRate)
        const oldRate = parseFloat(currencyRate) || 1;
        const newRate = parseFloat(rateToUse) || 1;
        const conversionFactor = oldRate / newRate;

        const convertPrice = (val: any): string => {
          const num = parseFloat(val) || 0;
          return (num * conversionFactor).toFixed(2);
        };

        // Prepare immutable updates for all rows, converting sales prices to the new currency
        const updatedRowsData = projectRawData.current.ROWS.map(
          (record: any) => {
            const item = record.ITEM;
            return {
              ...item,
              currency: tempCurrency,
              purchaseCurrency: tempCurrency,
              currencyRate: rateToUse,
              // Convert unit price and catalog price from old currency to new currency
              amount: convertPrice(item.amount),
              catalogPrice: convertPrice(item.catalogPrice),
              totalAmount: convertPrice(item.totalAmount),
              totalAmountWithVat: convertPrice(item.totalAmountWithVat),
            };
          },
        );
        //console.log("updatedRowsData", updatedRowsData);
        // Update all project items in parallel for better performance
        const updatePromises = updatedRowsData.map((record: any) => {
          const generatedItem = generateProjectProductItem(
            record.folder,
            record.project,
            "",
            record.part_number,
            record.product_name,
            record.quantity,
            record.amount, // converted unit price
            record.totalAmount, // converted total price
            record.totalAmountWithVat, // converted total with VAT
            tempCurrency, // Use the new currency
            record.vatValue,
            record.purchasePrice,
            record.attrib0,
            record.attrib0_id,
            record.attrib1,
            record.attrib1_id,
            record.attrib2,
            record.attrib2_id,
            record.attrib3,
            record.attrib3_id,
            record.attrib4,
            record.attrib4_id,
            record.attrib5,
            record.attrib5_id,
            record.attrib6,
            record.attrib6_id,
            record.attrib7,
            record.attrib7_id,
            record.attrib8,
            record.attrib8_id,
            record.attrib9,
            record.attrib9_id,
            record.supplier,
            record.refId,
            record.billingDate,
            record.billing_status,
          );

          return itemService.updateProjectItem(
            new UpdateProjectItemRequest(store.Server, store.SessionId, {
              ...generatedItem,
              ...record,
              currency: tempCurrency, // Explicitly override with new currency
              purchaseCurrency: tempCurrency, // Also update purchase currency
              currencyRate: rateToUse, // Explicitly set the new rate
              // Ensure converted prices are not overwritten by spread above
              amount: record.amount,
              catalogPrice: record.catalogPrice,
              totalAmount: record.totalAmount,
              totalAmountWithVat: record.totalAmountWithVat,
            }),
          );
        });

        // Wait for all updates to complete
        await Promise.all(updatePromises);

        // Update state only after all API calls succeed
        setRowsData(updatedRowsData);
        dispatch({ type: "UPDATE_CURRENCY_RATE", rate: rateToUse });
        dispatch({ type: "UPDATE_TEMP_RATE", rate: rateToUse });
        dispatch({ type: "UPDATE_SELECTED_CURRENCY", currency: tempCurrency });
        dispatch({
          type: "UPDATE_CALCULATION",
          field: "currency",
          value: tempCurrency,
        });
        if (updateFinal && pendingExpectedCloseDate) {
          dispatch({
            type: "UPDATE_FINAL_DATE",
            date: pendingExpectedCloseDate,
          });
        }

        // Show success message
        updateFinal &&
          ToastQueue.positive(
            t("exchange_rate_updated_successfully", {
              ns: "layout_components",
              defaultValue: "Exchange rate updated successfully",
            }),
            {
              timeout: 3000,
            },
          );

        // Reload fresh data from server
        await Promise.all([
          loadProjectItemCalculation(String(selectedItem.id)),
          loadProjectDetails(selectedItem.id as number),
          loadProjectItemGridData(),
        ]);

        return true;
      } catch (error) {
        console.error("Error updating exchange rate:", error);
        ToastQueue.negative(
          t("failed_to_update_exchange_rate", {
            ns: "layout_components",
            defaultValue: "Failed to update exchange rate. Please try again.",
          }),
          {
            timeout: 3000,
          },
        );
        return false;
      } finally {
        setShowLoader(false);
      }
    },
    [
      selectedItem?.id,
      store.Server,
      store.SessionId,
      projectItemCalculation,
      tempCurrency,
      tempRate,
      currencyRate,
      pendingExpectedCloseDate,
      itemService,
      loadProjectItemCalculation,
      loadProjectDetails,
      loadProjectItemGridData,
      t,
    ],
  );

  const updateProbabilityValue = useCallback(
    async (newProbability: string): Promise<boolean> => {
      if (!selectedItem?.id) {
        console.error("No selected item ID available");
        return false;
      }

      try {
        const resProjectItem = await itemService.updateProjectItemCalculation(
          new UpdateProjectItemCalculationRequest(
            store.Server,
            store.SessionId,
            projectItemCalculation?.id?.toString() || "",
            [
              {
                ...projectItemCalculation,
                projectId: selectedItem?.id?.toString() || "",
                probability: newProbability,
                modified: new Date(),
              } as any,
            ],
          ),
        );
        if (resProjectItem?.result === "OK") {
          ToastQueue.positive(
            t("probability_updated_successfully", {
              ns: "layout_components",
              defaultValue: "Probability updated successfully",
            }),
            {
              timeout: 3000,
            },
          );
        } else {
          ToastQueue.negative(
            t("failed_to_update_probability", {
              ns: "layout_components",
              defaultValue: "Failed to update probability",
            }),
            {
              timeout: 3000,
            },
          );
        }
        return true;
      } catch (error) {
        console.error("Error updating probability:", error);
        ToastQueue.negative(
          t("failed_to_update_probability", {
            ns: "layout_components",
            defaultValue: "Failed to update probability",
          }),
          { timeout: 3000 },
        );
        return false;
      }
    },
    [
      selectedItem?.id,
      itemService,
      store.Server,
      store.SessionId,
      projectItemCalculation,
      t,
    ],
  );

  const handleExpectedCloseDateChange = useCallback((date: any) => {
    const newDate = moment(date.toString()).format("YYYY-MM-DD");
    setPendingExpectedCloseDate(newDate);
    setShowExchangeRateDialog(true);
  }, []);

  const handleExchangeRateDialogYes = useCallback(
    async (shouldUpdateRate: boolean) => {
      try {
        if (shouldUpdateRate) {
          // Yes: Update exchange rate to the selected date, then update both date and rate
          if (projectItemCalculation.currency !== bccyCurrency) {
            const currency_id = currencies.find(
              (cur: any) => cur.code === projectItemCalculation.currency,
            )?.id;

            if (currency_id && pendingExpectedCloseDate) {
              const newRate = await getCurrencyRate(
                currency_id,
                pendingExpectedCloseDate + "T00:00:00",
              );
              dispatch({ type: "UPDATE_TEMP_RATE", rate: newRate.toString() });
              // Pass the new rate directly to avoid stale closure
              await updateExchangeRate(true, newRate.toString());
            }
          } else {
            // If BCCY, just update the date
            await updateExchangeRate(true);
          }
        } else {
          // No: Update only the date, keep current exchange rate
          if (!selectedItem?.id) {
            ToastQueue.negative(
              t("no_project_selected", {
                ns: "layout_components",
                defaultValue: "No project selected",
              }),
              { timeout: 3000 },
            );
            return;
          }

          setShowLoader(true);
          // Use pendingExpectedCloseDate if available, otherwise keep current finalDate
          const finalDateToUse =
            pendingExpectedCloseDate || projectItemCalculation.finalDate;

          const resProjectItem = await itemService.updateProjectItemCalculation(
            new UpdateProjectItemCalculationRequest(
              store.Server,
              store.SessionId,
              projectItemCalculation?.id?.toString() || "",
              [
                {
                  ...projectItemCalculation,
                  currency: projectItemCalculation.currency,
                  finalDate: finalDateToUse,
                  projectId: selectedItem.id.toString(),
                  modified: new Date(),
                } as any,
              ],
            ),
          );

          if (resProjectItem?.result === "OK") {
            if (pendingExpectedCloseDate) {
              dispatch({
                type: "UPDATE_FINAL_DATE",
                date: pendingExpectedCloseDate,
              });
            }
            ToastQueue.positive(
              t("expected_close_date_updated_successfully", {
                ns: "layout_components",
                defaultValue: "Expected close date updated successfully",
              }),
              {
                timeout: 3000,
              },
            );
            await loadProjectItemCalculation(String(selectedItem.id));
          } else {
            ToastQueue.negative(
              t("failed_to_update_expected_close_date", {
                ns: "layout_components",
                defaultValue: "Failed to update expected close date",
              }),
              {
                timeout: 3000,
              },
            );
          }
          setShowLoader(false);
        }
      } catch (error) {
        console.error("Error in handleExchangeRateDialogYes:", error);
        ToastQueue.negative(
          t("failed_to_update", {
            ns: "layout_components",
            defaultValue: "Failed to update",
          }),
          { timeout: 3000 },
        );
        setShowLoader(false);
      } finally {
        setShowExchangeRateDialog(false);
      }
    },
    [
      updateExchangeRate,
      projectItemCalculation,
      currencies,
      getCurrencyRate,
      pendingExpectedCloseDate,
      selectedItem,
      itemService,
      store,
      loadProjectItemCalculation,
      t,
      bccyCurrency,
    ],
  );

  // Currency rate dialog handlers
  const handleOpenCurrencyRateDialog = useCallback(() => {
    setShowCurrencyRateDialog(true);
  }, []);

  const handleApplyCurrencyRate = useCallback(async () => {
    try {
      setShowLoader(true);
      setShowCurrencyRateDialog(false);

      // Call updateExchangeRate with false to not update final date
      const success = await updateExchangeRate(false, tempRate);

      if (success) {
        ToastQueue.positive(
          t("currency_and_exchange_rate_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Currency and exchange rate updated successfully",
          }),
          {
            timeout: 3000,
          },
        );
      }
    } catch (error) {
      console.error("Error applying currency rate:", error);
      ToastQueue.negative(
        t("failed_to_update_currency_rate", {
          ns: "layout_components",
          defaultValue: "Failed to update currency rate",
        }),
        { timeout: 3000 },
      );
      setShowLoader(false);
    }
  }, [updateExchangeRate, tempRate, t]);

  const handleCancelCurrencyRate = useCallback(() => {
    dispatch({ type: "CANCEL_CURRENCY_RATE" });
    setShowCurrencyRateDialog(false);
  }, []);

  // Calculate Deal value in BCCY (bookkeeping currency)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dealValueBccy = useMemo(() => {
    const dealValue = parseFloat(
      projectItemCalculation?.amountWithoutVat || "0",
    );
    const rate = parseFloat(currencyRate || "1");
    if (projectItemCalculation?.currency === bccyCurrency) {
      return dealValue;
    }
    return dealValue * rate;
  }, [
    projectItemCalculation?.amountWithoutVat,
    projectItemCalculation?.currency,
    currencyRate,
    bccyCurrency,
  ]);

  const handleRowClose = (): void => {
    // setSelectedRow(defaultRowData);
    // setShowAddEditRow(false);
    // deviceInfo.shouldUseMobileLayout() && setIsOpen(false);
    // handleUnselect();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUnselect = () => {
    //setSelectedKeys(new Set());
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const deleteRecord = async (record: any) => {
    try {
      setShowLoader(true);
      //console.log('record', record);

      let result = await itemService.deleteProjectItem(
        new DeleteProjectItemRequest(store.Server, store.SessionId, record.id),
      );

      // console.log('result', result);
      if (result.result === "OK") {
        handleRowClose();
        ToastQueue.positive(
          t("record_deleted_successfully", {
            ns: "layout_components",
            defaultValue: "Record deleted successfully",
          }),
          { timeout: 3000 },
        );
      } else {
        ToastQueue.negative(
          t("failed_to_delete_record", {
            ns: "layout_components",
            defaultValue: "Failed to delete record",
          }),
          { timeout: 3000 },
        );
      }
    } catch (error) {
    } finally {
      setShowLoader(false);
    }
  };

  // Custom VAT Dropdown Editor Component
  const VATSelectEditor = ({
    value,
    onChange,
    onCommit,
    onCancel,
    shouldFocus,
  }: any) => {
    const selectRef = useRef<HTMLSelectElement>(null);

    useEffect(() => {
      if (shouldFocus && selectRef.current) {
        selectRef.current.focus();
      }
    }, [shouldFocus]);

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = parseFloat(e.target.value);
      // Only update the value (triggers calculation updates)
      // Do NOT commit - let user press Enter to save
      onChange(newValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      } else if (e.key === "Enter") {
        e.preventDefault();
        onCommit();
      }
    };

    return (
      <select
        ref={selectRef}
        value={value ?? 0}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        autoFocus={shouldFocus}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          height: "100%",
          border: "1px solid #0078d4",
          borderRadius: "4px",
          padding: "4px 8px",
          fontSize: "14px",
          backgroundColor: "white",
          cursor: "pointer",
        }}
      >
        <option value={0}>DPH 0%</option>
        <option value={10}>DPH 10%</option>
        <option value={15}>DPH 15%</option>
        <option value={21}>DPH 21%</option>
      </select>
    );
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={"center"} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <>
        <div ref={mainContainerRef}>
          <View UNSAFE_className="detail" key={`detail-${responsiveKey}`}>
            <View
              backgroundColor="gray-100"
              padding={{ base: "size-150", S: "size-200", M: "size-200" }}
              UNSAFE_style={{ marginTop: "15px", borderRadius: "12px" }}
            >
              <Flex direction="column" gap={{ base: "size-100", M: "size-50" }}>
                {/* Row 1: Block 1 (Expected close date, Probability, Currency) | Block 2 (Deal value BCCY, Deal value) */}
                <View
                  UNSAFE_style={{
                    display: "grid",
                    gridTemplateColumns:
                      isMobile || mainContainerWidth < 720
                        ? "1fr"
                        : "minmax(0, 1fr) minmax(0, 1fr)",
                    gap: "24px",
                    alignItems: "end",
                  }}
                >
                  {/* Block 1: Expected close date, Probability, Currency */}
                  <View
                    UNSAFE_style={{
                      display: "grid",
                      gridTemplateColumns:
                        isMobile || mainContainerWidth < 720
                          ? "1fr"
                          : "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 0.8fr)",
                      gap: "12px",
                      alignItems: "end",
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    <View>
                      <Provider locale="en-US">
                        <DatePicker
                          id="expectedCloseDate"
                          UNSAFE_className={"datePicker"}
                          label={t("expected_close_date", {
                            ns: "layout_components",
                            defaultValue: "Expected Close Date",
                          })}
                          width="100%"
                          value={
                            projectItemCalculation?.finalDate
                              ? parseDate(
                                  moment(
                                    projectItemCalculation?.finalDate,
                                    "YYYY-MM-DD",
                                  ).format("YYYY-MM-DD"),
                                )
                              : undefined
                          }
                          onChange={handleExpectedCloseDateChange}
                        />
                      </Provider>
                    </View>

                    <View>
                      <NumericTextField
                        label={t("probability", {
                          ns: "layout_components",
                          defaultValue: "Probability",
                        })}
                        width="100%"
                        value={
                          projectItemCalculation?.probability
                            ?.toString()
                            .replace("%", "")
                            .trim() || ""
                        }
                        onChange={(value: any) => {
                          dispatch({
                            type: "UPDATE_CALCULATION",
                            field: "probability",
                            value,
                          });
                          setProbabilityTerm(value);
                        }}
                        prefix="%"
                      />
                    </View>

                    <View>
                      <NumericTextField
                        isDisabled
                        label={t("currency", {
                          ns: "layout_components",
                          defaultValue: "Currency",
                        })}
                        width="100%"
                        value={selectedCurrency || bccyCurrency}
                      />
                      {/* <TextField
                      label={t("currency", {
                        ns: "layout_components",
                        defaultValue: "Currency",
                      })}
                      width="100%"
                      value={selectedCurrency || bccyCurrency}
                      UNSAFE_className={styles.disabled}
                    /> */}
                    </View>
                  </View>
                  {/* Block 2: Deal value BCCY, Deal value */}
                  <View
                    UNSAFE_style={{
                      display: "grid",
                      gridTemplateColumns:
                        isMobile || mainContainerWidth < 720
                          ? "1fr"
                          : selectedCurrency !== bccyCurrency
                            ? "1fr 1fr"
                            : "1fr",
                      gap: "16px",
                      alignItems: "end",
                    }}
                  >
                    {selectedCurrency !== bccyCurrency && (
                      <View>
                        <NumericTextField
                          label={`${t("deal_value", {
                            ns: "layout_components",
                            defaultValue: "Deal Value 1",
                          })} ${bccyCurrency}`}
                          width="100%"
                          value={formatDecimalToString(
                            projectItemCalculation?.amountWithoutVatBccy
                              ? Number.parseFloat(
                                  projectItemCalculation?.amountWithoutVatBccy,
                                ).toFixed(2)
                              : "0",
                          )}
                          isReadOnly
                          thousandsSeparator={true}
                        />
                      </View>
                    )}

                    <View>
                      <Flex direction="row" alignItems="end" gap="size-100">
                        <View flex="1">
                          <NumericTextField
                            label={t("deal_value", {
                              ns: "layout_components",
                              defaultValue: "Deal value",
                            })}
                            width="100%"
                            value={
                              projectItemCalculation?.amountWithoutVat
                                ? formatDecimalToString(
                                    Number.parseFloat(
                                      projectItemCalculation?.amountWithoutVat,
                                    ).toFixed(2),
                                  )
                                : "0"
                            }
                            onChange={(value: any) => {
                              dispatch({ type: "UPDATE_DEAL_VALUE", value });
                            }}
                            onKeyDown={onEnterKey(handleSaveDealValue)}
                            prefix={
                              projectItemCalculation?.currency || bccyCurrency
                            }
                            thousandsSeparator={true}
                          />
                        </View>
                        {changeFlags.isDealValueChanged && (
                          <Flex direction="row" gap="size-50">
                            <ActionButton
                              id="btn_handleSaveDealValue"
                              isQuiet
                              onPress={handleSaveDealValue}
                              UNSAFE_style={{
                                minWidth: "auto",
                                padding: "4px",
                              }}
                            >
                              <Checkmark size="S" />
                            </ActionButton>
                            <ActionButton
                              id="btn_handleCancelDealValue"
                              isQuiet
                              onPress={handleCancelDealValue}
                              UNSAFE_style={{
                                minWidth: "auto",
                                padding: "4px",
                              }}
                            >
                              <Close size="S" />
                            </ActionButton>
                          </Flex>
                        )}
                      </Flex>
                    </View>
                  </View>
                </View>

                {/* Exchange Rate Row - right-aligned, below the field row */}
                <Flex
                  direction="row"
                  alignItems="center"
                  justifyContent="end"
                  alignContent={"end"}
                >
                  <Text
                    UNSAFE_style={{
                      color: "rgb(25, 118, 210)",
                      fontSize: "14px",
                    }}
                  >
                    {t("exchange_rate", {
                      ns: "layout_components",
                      defaultValue: "Exchange rate",
                    })}
                    {" "}
                    {rowsData.length > 0
                      ? formatDecimalToString(
                          parseFloat(currencyRate).toFixed(3),
                        )
                      : "1.000"}
                  </Text>
                  <ActionButton
                    isQuiet
                    onPress={handleOpenCurrencyRateDialog}
                    UNSAFE_style={{
                      minWidth: "auto",
                      color: "rgb(25, 118, 210)",
                      fontSize: "14px",
                      textDecoration: "underline",
                    }}
                  >
                    <Text
                      UNSAFE_style={{
                        color: "rgb(25, 118, 210)",
                        fontSize: "14px",
                        textDecoration: "underline",
                      }}
                    >
                      [
                      {t("edit", {
                        ns: "layout_components",
                        defaultValue: "Edit",
                      })}
                      ]
                    </Text>
                  </ActionButton>
                </Flex>

                <Divider size="S" />

                {/* Row 2: Block 3 (Margin/Loss graphic) | Block 4 (Purchase cost BCCY, Purchase cost) */}
                <View
                  UNSAFE_style={{
                    display: "grid",
                    gridTemplateColumns:
                      isMobile || mainContainerWidth < 720
                        ? "1fr"
                        : "minmax(0, 1fr) minmax(0, 1fr)",
                    gap: "24px",
                    alignItems: "end",
                  }}
                >
                  {/* Block 3: Margin Section */}
                  <View>
                    {showMarginGraphic ? (
                      <div
                        style={{ width: "100%", cursor: "pointer" }}
                        onClick={() => setShowMarginGraphic(false)}
                      >
                        <Flex
                          direction={{ base: "column", S: "row" }}
                          justifyContent="space-between"
                          gap="size-100"
                          marginBottom="size-100"
                        >
                          <Flex
                            direction="row"
                            gap="size-100"
                            alignItems="center"
                          >
                            <Text
                              UNSAFE_style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                              }}
                            >
                              {grossProfitBccy >= 0
                                ? t("gross_profit", {
                                    ns: "layout_components",
                                    defaultValue: "Gross Profit",
                                  })
                                : t("loss", {
                                    ns: "layout_components",
                                    defaultValue: "Loss",
                                  })}
                            </Text>
                            <Text
                              UNSAFE_style={{
                                fontWeight: "bold",
                                color:
                                  grossProfitBccy >= 0 ? "#1976d2" : "#d32f2f",
                                fontSize: "14px",
                              }}
                            >
                              {bccyCurrency} {formatCurrency(grossProfitBccy)}
                            </Text>
                          </Flex>
                          <Flex
                            direction="row"
                            gap="size-100"
                            alignItems="center"
                          >
                            <Text UNSAFE_style={{ fontSize: "14px" }}>
                              {t("margin", {
                                ns: "layout_components",
                                defaultValue: "Margin",
                              })}
                            </Text>
                            <Text
                              UNSAFE_style={{
                                fontWeight: "bold",
                                color:
                                  marginPercentage >= 0 ? "#1976d2" : "#d32f2f",
                                fontSize: "14px",
                              }}
                            >
                              {formatCurrency(marginPercentage)}%
                            </Text>
                          </Flex>
                        </Flex>
                        <View
                          height="size-200"
                          borderRadius="large"
                          UNSAFE_style={{
                            position: "relative",
                            overflow: "hidden",
                            backgroundColor:
                              marginPercentage >= 0 ? "#e3f2fd" : "#ffebee",
                          }}
                        >
                          <View
                            UNSAFE_style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              backgroundColor:
                                marginPercentage >= 0 ? "#1976d2" : "#d32f2f",
                              width: `${barWidthPercentage}%`,
                              height: "100%",
                              transition:
                                "width 0.3s ease-in-out, background-color 0.3s ease-in-out",
                            }}
                          />
                        </View>
                      </div>
                    ) : (
                      <View
                        UNSAFE_style={{
                          display: "grid",
                          gridTemplateColumns:
                            isMobile || mainContainerWidth < 720
                              ? "1fr"
                              : selectedCurrency !== bccyCurrency
                                ? "minmax(60px, 0.8fr) minmax(90px, 1fr) minmax(90px, 1fr) auto"
                                : "minmax(80px, 1fr) minmax(120px, 1.5fr) auto",
                          gap: "16px",
                          alignItems: "end",
                        }}
                      >
                        <View>
                          <NumericTextField
                            label={t("margin_percentage", {
                              ns: "layout_components",
                              defaultValue: "Margin %",
                            })}
                            width="100%"
                            value={formatCurrency(marginPercentage)}
                            onChange={(value: any) => {
                              const purchaseCost =
                                (parseFloat(
                                  projectItemCalculation.totalPurchase,
                                ) || 0) * (parseFloat(currencyRate) || 1);
                              const rate = parseFloat(currencyRate) || 1;
                              dispatch({
                                type: "UPDATE_MARGIN_PERCENTAGE",
                                value,
                                purchaseCost,
                                rate,
                              });
                            }}
                            onKeyDown={onEnterKey(handleSaveMargin)}
                            prefix="%"
                          />
                        </View>
                        {selectedCurrency !== bccyCurrency && (
                          <View>
                            <NumericTextField
                              label={t("margin_bccy", {
                                ns: "layout_components",
                                defaultValue: "Margin (BCCY)",
                              })}
                              width="100%"
                              value={formatCurrency(marginValueBccy)}
                              isReadOnly
                              thousandsSeparator={true}
                            />
                          </View>
                        )}
                        <View>
                          <NumericTextField
                            label={t("margin_value", {
                              ns: "layout_components",
                              defaultValue: "Margin value",
                            })}
                            width="100%"
                            value={formatCurrency(marginValue)}
                            onChange={(value: any) => {
                              // Purchase cost in original currency (e.g., EUR)
                              const purchaseCost =
                                parseFloat(
                                  projectItemCalculation.totalPurchase,
                                ) || 0;
                              dispatch({
                                type: "UPDATE_MARGIN_VALUE",
                                value,
                                purchaseCost,
                              });
                            }}
                            onKeyDown={onEnterKey(handleSaveMargin)}
                            thousandsSeparator={true}
                          />
                        </View>
                        <View
                          UNSAFE_style={{
                            display: "flex",
                            alignItems: "flex-end",
                            height: "100%",
                          }}
                        >
                          {changeFlags.isMarginPercentageChanged ||
                          changeFlags.isMarginValueChanged ? (
                            <Flex direction="row" gap="size-50">
                              <ActionButton
                                id="btn_handleSaveMargin"
                                isQuiet
                                onPress={handleSaveMargin}
                                UNSAFE_style={{
                                  minWidth: "auto",
                                  padding: "4px",
                                }}
                              >
                                <Checkmark size="S" />
                              </ActionButton>
                              <ActionButton
                                id="btn_handleCancelMargin"
                                isQuiet
                                onPress={handleCancelMargin}
                                UNSAFE_style={{
                                  minWidth: "auto",
                                  padding: "4px",
                                }}
                              >
                                <Close size="S" />
                              </ActionButton>
                            </Flex>
                          ) : (
                            <ActionButton
                              isQuiet
                              onPress={() => setShowMarginGraphic(true)}
                              UNSAFE_style={{
                                minWidth: "auto",
                                padding: "4px",
                              }}
                            >
                              <GraphBarVertical size="S" />
                            </ActionButton>
                          )}
                        </View>
                      </View>
                    )}
                  </View>

                  {/* Block 4: Purchase Cost Section */}
                  <View
                    UNSAFE_style={{
                      display: "grid",
                      gridTemplateColumns:
                        isMobile || mainContainerWidth < 720
                          ? "1fr"
                          : selectedCurrency !== bccyCurrency
                            ? "1fr 1fr"
                            : "1fr",
                      gap: "16px",
                      alignItems: "end",
                    }}
                  >
                    {selectedCurrency !== bccyCurrency && (
                      <View>
                        <NumericTextField
                          label={`${t("purchase_cost", {
                            ns: "layout_components",
                            defaultValue: "Purchase cost",
                          })} ${bccyCurrency}`}
                          width="100%"
                          value={
                            projectItemCalculation?.totalPurchase
                              ? formatDecimalToString(
                                  (
                                    Number.parseFloat(
                                      projectItemCalculation?.totalPurchase,
                                    ) * (rowsData[0]?.currencyRate || 1)
                                  ).toFixed(2),
                                )
                              : "0"
                          }
                          isReadOnly
                          thousandsSeparator={true}
                        />
                      </View>
                    )}
                    <Flex direction="row" alignItems="end" gap="size-100">
                      <View flex="1">
                        <NumericTextField
                          label={t("purchase_cost", {
                            ns: "layout_components",
                            defaultValue: "Purchase cost",
                          })}
                          width="100%"
                          value={
                            projectItemCalculation?.totalPurchase
                              ? formatDecimalToString(
                                  Number.parseFloat(
                                    projectItemCalculation?.totalPurchase,
                                  ).toFixed(2),
                                )
                              : "0"
                          }
                          onChange={(value: any) => {
                            dispatch({ type: "UPDATE_PURCHASE_COST", value });
                          }}
                          onKeyDown={onEnterKey(handleSavePurchaseCost)}
                          prefix={
                            projectItemCalculation?.currency || bccyCurrency
                          }
                          thousandsSeparator={true}
                        />
                      </View>
                      {changeFlags.isPurchaseCostChanged && (
                        <Flex direction="row" gap="size-50">
                          <ActionButton
                            id="btn_handleSavePurchaseCost"
                            isQuiet
                            onPress={handleSavePurchaseCost}
                            UNSAFE_style={{ minWidth: "auto", padding: "4px" }}
                          >
                            <Checkmark size="S" />
                          </ActionButton>
                          <ActionButton
                            id="btn_handleCancelPurchaseCost"
                            isQuiet
                            onPress={handleCancelPurchaseCost}
                            UNSAFE_style={{ minWidth: "auto", padding: "4px" }}
                          >
                            <Close size="S" />
                          </ActionButton>
                        </Flex>
                      )}
                    </Flex>
                  </View>
                </View>

                <Divider size="S" />

                {/* Row 3: Block 5 (Discount, Discount BCCY, Discount value) | Block 6 (Catalog price BCCY, Catalog price) */}
                <View
                  UNSAFE_style={{
                    display: "grid",
                    gridTemplateColumns:
                      isMobile || mainContainerWidth < 720
                        ? "1fr"
                        : "minmax(0, 1fr) minmax(0, 1fr)",
                    gap: "24px",
                    alignItems: "end",
                  }}
                >
                  {/* Block 5: Discount Section */}
                  <View
                    UNSAFE_style={{
                      display: "grid",
                      gridTemplateColumns:
                        isMobile || mainContainerWidth < 720
                          ? "1fr"
                          : selectedCurrency !== bccyCurrency
                            ? "minmax(60px, 0.8fr) minmax(90px, 1fr) minmax(180px, 1.4fr)"
                            : "minmax(80px, 1fr) minmax(200px, 1.5fr)",
                      gap: "16px",
                      alignItems: "end",
                      overflow: "visible",
                    }}
                  >
                    <View>
                      <NumericTextField
                        label={t("discount", {
                          ns: "layout_components",
                          defaultValue: "Discount",
                        })}
                        width="100%"
                        value={discountPercentageInput}
                        onChange={(value: any) => {
                          const catalogPrice =
                            parseFloat(
                              projectItemCalculation.catalogPriceBccy,
                            ) || 0;
                          dispatch({
                            type: "UPDATE_DISCOUNT_PERCENTAGE",
                            value,
                            catalogPrice,
                          });
                        }}
                        onKeyDown={onEnterKey(handleSaveCatalogPrice)}
                        prefix="%"
                      />
                    </View>

                    {selectedCurrency !== bccyCurrency && (
                      <View>
                        <NumericTextField
                          label={`${t("discount", {
                            ns: "layout_components",
                            defaultValue: "Discount",
                          })} ${bccyCurrency}`}
                          width="100%"
                          value={discountValueBccyInput}
                          onChange={(value: any) => {
                            const catalogPrice =
                              parseFloat(
                                projectItemCalculation.catalogPriceBccy,
                              ) || 0;
                            dispatch({
                              type: "UPDATE_DISCOUNT_VALUE",
                              value,
                              catalogPrice,
                            });
                          }}
                          onKeyDown={onEnterKey(handleSaveCatalogPrice)}
                          thousandsSeparator={true}
                        />
                      </View>
                    )}

                    <Flex
                      direction="row"
                      alignItems="end"
                      gap="size-100"
                      UNSAFE_style={{ overflow: "visible" }}
                    >
                      <View flex="1" minWidth="size-1200">
                        <NumericTextField
                          label={t("discount_value", {
                            ns: "layout_components",
                            defaultValue: "Discount value",
                          })}
                          width="100%"
                          value={discountValueInput}
                          onChange={(value: any) => {
                            const catalogPrice =
                              parseFloat(
                                projectItemCalculation.catalogPriceBccy,
                              ) || 0;
                            dispatch({
                              type: "UPDATE_DISCOUNT_VALUE_SELECTED_CCY",
                              value,
                              catalogPrice,
                            });
                          }}
                          onKeyDown={onEnterKey(handleSaveCatalogPrice)}
                          thousandsSeparator={true}
                        />
                      </View>
                      {(changeFlags.isDiscountChanged ||
                        changeFlags.isDiscountValueChanged ||
                        changeFlags.isCatalogPriceChanged) && (
                        <Flex
                          direction="row"
                          gap="size-25"
                          flexShrink={0}
                          alignItems="center"
                          UNSAFE_style={{
                            minWidth: "56px",
                            marginBottom: "6px",
                          }}
                        >
                          <ActionButton
                            id="btn_handleSaveCatalogPrice"
                            isQuiet
                            onPress={handleSaveCatalogPrice}
                            UNSAFE_style={{
                              minWidth: "24px",
                              width: "24px",
                              padding: "2px",
                            }}
                          >
                            <Checkmark size="S" />
                          </ActionButton>
                          <ActionButton
                            id="btn_handleCancelCatalogPrice"
                            isQuiet
                            onPress={handleCancelCatalogPrice}
                            UNSAFE_style={{
                              minWidth: "24px",
                              width: "24px",
                              padding: "2px",
                            }}
                          >
                            <Close size="S" />
                          </ActionButton>
                        </Flex>
                      )}
                    </Flex>
                  </View>

                  {/* Block 6: Catalog Price Section */}
                  <View
                    UNSAFE_style={{
                      display: "grid",
                      gridTemplateColumns:
                        isMobile || mainContainerWidth < 720
                          ? "1fr"
                          : selectedCurrency !== bccyCurrency
                            ? "1fr 1fr"
                            : "1fr",
                      gap: "16px",
                      alignItems: "end",
                    }}
                  >
                    {selectedCurrency !== bccyCurrency && (
                      <View>
                        <NumericTextField
                          label={`${t("catalog_price", {
                            ns: "layout_components",
                            defaultValue: "Catalog price",
                          })} ${bccyCurrency}`}
                          width="100%"
                          value={
                            projectItemCalculation?.catalogPriceBccy
                              ? formatDecimalToString(
                                  Number.parseFloat(
                                    projectItemCalculation?.catalogPriceBccy,
                                  ).toFixed(2),
                                )
                              : "0"
                          }
                          isReadOnly
                          thousandsSeparator={true}
                        />
                      </View>
                    )}

                    <View>
                      <NumericTextField
                        label={t("catalog_price", {
                          ns: "layout_components",
                          defaultValue: "Catalog price",
                        })}
                        width="100%"
                        value={
                          projectItemCalculation?.catalogPrice
                            ? formatDecimalToString(
                                Number.parseFloat(
                                  projectItemCalculation?.catalogPrice,
                                ).toFixed(2),
                              )
                            : "0"
                        }
                        onChange={(value: any) => {
                          dispatch({ type: "UPDATE_CATALOG_PRICE", value });
                        }}
                        isReadOnly
                        prefix={
                          projectItemCalculation?.currency || bccyCurrency
                        }
                        thousandsSeparator={true}
                      />
                    </View>
                  </View>
                </View>

                <Divider size="S" />

                {/* Row 4: Block 7 (Provider) | Block 8 (Invoice To) */}
                <View
                  UNSAFE_style={{
                    display: "grid",
                    gridTemplateColumns:
                      isMobile || mainContainerWidth < 720
                        ? "1fr"
                        : "minmax(0, 1fr) minmax(0, 1fr)",
                    gap: "24px",
                  }}
                >
                  {/* Block 7: Provider */}
                  <View>
                    <ComboBox
                      UNSAFE_className={"comboBox"}
                      label={t("provider", {
                        ns: "layout_components",
                        defaultValue: "Provider",
                      })}
                      width="100%"
                      placeholder={t("type_to_search", { ns: "recruitment" })}
                      items={providerList.items}
                      inputValue={providerInputValue}
                      onInputChange={(value) => {
                        setProviderInputValue(value);
                        providerList.setFilterText(value);
                      }}
                      loadingState={providerList.loadingState}
                      selectedKey={selectedProviderId}
                      onSelectionChange={(key) => {
                        if (key) {
                          const keyStr = String(key);
                          const selected: any = providerList.items.find(
                            (p: any) => p.id === keyStr,
                          );
                          if (selected) {
                            setProviderInputValue(selected.name);
                            if (selectedProviderId !== keyStr) {
                              setSelectedProviderId(keyStr);
                              updateProjectProvider(selected.id);
                            }
                          }
                        } else {
                          setSelectedProviderId(null);
                          setProviderInputValue("");
                        }
                      }}
                      allowsCustomValue
                      menuTrigger="input"
                    >
                      {(item: any) => <Item key={item.id}>{item.name}</Item>}
                    </ComboBox>
                  </View>

                  {/* Block 8: Invoice To */}
                  <View>
                    <ComboBox
                      UNSAFE_className={"comboBox"}
                      label={t("invoice_to", {
                        ns: "layout_components",
                        defaultValue: "Invoice To",
                      })}
                      width="100%"
                      placeholder={t("type_to_search", { ns: "recruitment" })}
                      items={invoiceToList.items}
                      inputValue={invoiceToInputValue}
                      onInputChange={(value) => {
                        setInvoiceToInputValue(value);
                        invoiceToList.setFilterText(value);
                      }}
                      loadingState={invoiceToList.loadingState}
                      selectedKey={selectedInvoiceToId}
                      onSelectionChange={(key) => {
                        if (key) {
                          const keyStr = String(key);
                          const selected: any = invoiceToList.items.find(
                            (i: any) => i.id === keyStr,
                          );
                          if (selected) {
                            setInvoiceToInputValue(selected.name);
                            if (selectedInvoiceToId !== keyStr) {
                              setSelectedInvoiceToId(keyStr);
                              updateProjectInvoiceTo(selected.id);
                            }
                          }
                        } else {
                          setSelectedInvoiceToId(null);
                          setInvoiceToInputValue("");
                        }
                      }}
                      allowsCustomValue
                      menuTrigger="input"
                    >
                      {(item: any) => <Item key={item.id}>{item.name}</Item>}
                    </ComboBox>
                  </View>
                </View>
              </Flex>
            </View>

            {/* <Flex direction="row" justifyContent="space-between" gap="size-300" marginTop="size-300">
            <Flex direction="row" gap="size-200">
              <Button variant="secondary">
                <Text>Add</Text>
              </Button>
              <Button variant="negative">
                <Text>Delete</Text>
              </Button>
            </Flex>
            <Flex direction="row" gap="size-200">
              <Button variant="secondary">
                <Text>Save</Text>
              </Button>
              <Button variant="cta">
                <Text>Delivery Dashboard</Text>
              </Button>
            </Flex>
          </Flex> */}

            <View marginTop="size-225" position="relative">
              {/* <Flex direction="row" justifyContent="space-between" alignItems="center">
              <Flex direction="row" gap="size-200" alignItems="center">
                <Text>Total:</Text>
                <Text UNSAFE_style={{ fontWeight: 'bold' }}>{`1,000.00 ${bccyCurrency}`}</Text>
              </Flex>
              <Flex direction="row" gap="size-200" alignItems="center">
                <Text>Total:</Text>
                <Text UNSAFE_style={{ fontWeight: 'bold' }}>{`1,000.00 ${bccyCurrency}`}</Text>
              </Flex>
            </Flex> */}

              <View marginTop="size-225">
                <View
                  position={"relative"}
                  width={"100%"}
                  UNSAFE_style={{ minHeight: treeGridMaxHeight }}
                >
                  <Flex direction={"column"} gap={"size-150"} width={"100%"}>
                    <Flex
                      direction={"row"}
                      alignItems={"start"}
                      justifyContent={"start"}
                    >
                      <Content
                        position={"relative"}
                        UNSAFE_className={styles.heading_text}
                      >
                        {componentTitle}
                      </Content>
                      <Flex
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        gap={"size-100"}
                        UNSAFE_className={styles.icon_add_parent}
                      >
                        <Content>
                          <></>
                        </Content>
                      </Flex>
                    </Flex>
                    <Flex
                      direction={"column"}
                      width={"100%"}
                      UNSAFE_style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                      }}
                    >
                      <div
                        ref={(el) => {
                          componentRef.current = el;
                          treeGridContainerRef.current = el;
                        }}
                      >
                        <TreeGridComponent
                          title=""
                          gridId="project-item-grid"
                          loading={showLoader}
                          emptyMessage={t(
                            "no_items_found_click_add_item_to_get_started",
                            {
                              ns: "layout_components",
                              defaultValue:
                                "No items found. Click 'Add item' to get started.",
                            },
                          )}
                          columns={
                            [
                              {
                                key: "productName",
                                header: t("product", {
                                  ns: "layout_components",
                                  defaultValue: "Product",
                                }),
                                width: 200,
                                editable: true,
                                editorType: "combobox",
                                isTreeColumn: true,
                                showFolderIcon: true,
                                comboBoxConfig: {
                                  placeholder: t(
                                    "search_products_by_name_or_sku",
                                    {
                                      ns: "layout_components",
                                      defaultValue:
                                        "Search products by name or SKU...",
                                    },
                                  ),

                                  menuWidth: 450,
                                  debounceMs: 250,
                                  minSearchLength: 2,
                                  loadItems: searchProducts,
                                  groupBy: (item) => item.category || "Other",
                                  getDisplayValue: (item) =>
                                    item.productName || item.label,
                                  getValue: (item) =>
                                    item.productName || item.label,
                                  onSelectionChange: (item, row) => {
                                    if (!item) return {};
                                    const qty = row.data.quantity || 1;
                                    const unitPrice = item.unitPrice || 0;
                                    const totalPrice = qty * unitPrice;
                                    const vatValue = item.vatValue || 0;
                                    const totalWithVat =
                                      totalPrice * (1 + vatValue / 100);
                                    // Destructure out `id` to avoid overwriting the row's id
                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                    const { id: _productId, ...itemWithoutId } =
                                      item;
                                    return {
                                      ...itemWithoutId,
                                      productName:
                                        item.productName || item.label,
                                      name: item.productName || item.label,
                                      description:
                                        item.productName || item.label,
                                      refId: item.sku || "",
                                      partID: item.productId,
                                      unitPrice: unitPrice,
                                      unitPriceCurr: `${formatDecimalToString(unitPrice.toFixed(2))} ${item.currency || bccyCurrency}`,
                                      amountWithVat:
                                        item.amountWithVat ||
                                        item.catalogPrice ||
                                        0,
                                      vatValue: vatValue,
                                      vatPercent: `${vatValue}%`,
                                      purchasePrice: item.purchasePrice || 0,
                                      currency: item.currency || bccyCurrency,
                                      totalPriceCurr: `${formatDecimalToString(totalPrice.toFixed(2))} ${item.currency || bccyCurrency}`,
                                      totalAmountWithVatCurr: `${formatDecimalToString(totalWithVat.toFixed(2))} ${item.currency || bccyCurrency}`,
                                    };
                                  },
                                },
                              },
                              {
                                key: "name",
                                header: t("name", {
                                  ns: "layout_components",
                                  defaultValue: "Name",
                                }),

                                width: 200,
                                editable: true,
                                editorType: "text",
                                align: "left" as const,
                              },
                              {
                                key: "unitPrice",
                                header: t("unit_price", {
                                  ns: "layout_components",
                                  defaultValue: "Unit Price",
                                }),
                                width: 150,
                                editable: true,
                                editorType: "number",
                                align: "right" as const,
                                numberFormat: {
                                  decimals: 2,
                                },
                              },
                              {
                                key: "quantity",
                                header: t("qty", {
                                  ns: "layout_components",
                                  defaultValue: "Qty.",
                                }),

                                width: 100,
                                editable: true,
                                editorType: "number",
                                align: "right",
                                numberFormat: {
                                  decimals: 0,
                                },
                              },
                              {
                                key: "vatValue",
                                header: t("vat", {
                                  ns: "layout_components",
                                  defaultValue: "VAT",
                                }),
                                width: 120,
                                editable: true,
                                editorType: "custom",
                                customEditor: VATSelectEditor,
                                align: "right" as const,
                                renderCell: (value: any) => `DPH ${value}%`,
                              },
                              {
                                key: "totalPriceCurr",
                                header: t("excl_vat", {
                                  ns: "layout_components",
                                  defaultValue: "excl. VAT",
                                }),
                                width: 150,
                                editable: false,
                                align: "right" as const,
                              },
                              {
                                key: "totalAmountWithVatCurr",
                                header: t("incl_vat", {
                                  ns: "layout_components",
                                  defaultValue: "incl. VAT",
                                }),
                                width: 150,
                                editable: false,
                                align: "right" as const,
                              },
                            ] as TreeGridColumn[]
                          }
                          data={rowsData.map(
                            (item: any, index: number) =>
                              ({
                                id: item.id || `row-${index}`,
                                parentId: item.parentId || null,
                                depth: item.depth || 0,
                                childrenIds: item.childrenIds || [],
                                isExpanded: item.isExpanded !== false,
                                rowType: "product" as const,
                                sortOrder: item.sortOrder ?? index,
                                data: item,
                              }) as TreeGridRow,
                          )}
                          showAddIcon={true}
                          showCancelIcon={true}
                          /** Add Click will call the same funtion when press enter key */
                          onAddClick={() => {}}
                          /** Cancel Click will call the same funtion when press escape key */
                          onCancelClick={() => {}}
                          showSelection={true}
                          showSelectAll={true}
                          enableDragDrop={true}
                          enableKeyboardNav={true}
                          enableInlineEdit={true}
                          enableIndentOutdent={true}
                          onSelectionChange={(selectedIds: string[]) => {
                            // Update selection state using reducer
                            dispatchSelection({
                              type: "SET_SELECTED_IDS",
                              selectedIds: selectedIds,
                            });
                          }}
                          onRowCreate={handleTreeGridRowCreate}
                          onRowMove={handleRowMove}
                          getNewRowDefaultData={() => ({
                            isNew: true,
                            productName: "",
                            name: "",
                            description: "",
                            quantity: 1,
                            unitPrice: 0,
                            totalPriceCurr:
                              "0 " +
                              (projectItemCalculation.currency || bccyCurrency),
                            unitPriceCurr:
                              "0 " +
                              (projectItemCalculation.currency || bccyCurrency),
                            totalAmountWithVatCurr:
                              "0 " +
                              (projectItemCalculation.currency || bccyCurrency),
                            currency:
                              projectItemCalculation.currency || bccyCurrency,
                            vatValue: 0,
                            vatPercent: "0%",
                            amountWithVat: 0,
                            purchasePrice: 0,
                            partID: "",
                            refId: "",
                          })}
                          onRowUpdate={handleTreeGridRowUpdate}
                          onCellValueChange={(rowId, columnKey, value) => {
                            // Update local state immediately for responsive UI
                            setRowsData((prev) => {
                              // Guard: skip if row not found (it's still only in TreeGrid internal state)
                              const rowExists = prev.some(
                                (item) => item.id === rowId,
                              );
                              if (!rowExists) return prev;

                              return prev.map((item) => {
                                if (item.id === rowId) {
                                  const updated = {
                                    ...item,
                                    [columnKey]: value,
                                  };
                                  // Recalculate totals if quantity, unitPrice, or vatValue changes
                                  if (
                                    columnKey === "quantity" ||
                                    columnKey === "unitPrice" ||
                                    columnKey === "vatValue"
                                  ) {
                                    const qty =
                                      columnKey === "quantity"
                                        ? parseFloat(value) || 1
                                        : parseFloat(item.quantity) || 1;
                                    const unitPrice =
                                      columnKey === "unitPrice"
                                        ? parseFloat(value) || 0
                                        : parseFloat(item.unitPrice) || 0;
                                    const vatValue =
                                      columnKey === "vatValue"
                                        ? parseFloat(value) || 0
                                        : parseFloat(item.vatValue) || 0;
                                    const totalPrice = qty * unitPrice;
                                    const totalWithVat =
                                      totalPrice * (1 + vatValue / 100);
                                    updated.unitPrice = unitPrice;
                                    updated.unitPriceCurr = `${formatDecimalToString(unitPrice.toFixed(2))} ${item.currency || bccyCurrency}`;
                                    updated.totalPrice = formatDecimalToString(
                                      totalPrice.toFixed(2),
                                    );
                                    updated.totalPriceCurr = `${formatDecimalToString(totalPrice.toFixed(2))} ${item.currency || bccyCurrency}`;
                                    updated.totalAmountWithVat = totalWithVat;
                                    updated.totalAmountWithVatCurr = `${formatDecimalToString(totalWithVat.toFixed(2))} ${item.currency || bccyCurrency}`;
                                    updated.vatValue = vatValue;
                                    updated.vatPercent = `${vatValue}%`;
                                  }
                                  return updated;
                                }
                                return item;
                              });
                            });
                          }}
                          onMultiFieldUpdate={(rowId, changes) => {
                            // Update local state when ComboBox selects a product
                            setRowsData((prev) => {
                              // Guard: skip if row not found (it's still only in TreeGrid internal state)
                              const rowExists = prev.some(
                                (item) => item.id === rowId,
                              );
                              if (!rowExists) return prev;

                              return prev.map((item) => {
                                if (item.id === rowId) {
                                  const qty =
                                    changes.quantity ?? item.quantity ?? 1;
                                  const unitPrice =
                                    changes.unitPrice ?? item.unitPrice ?? 0;
                                  const totalPrice = qty * unitPrice;
                                  const vatValue =
                                    changes.vatValue ?? item.vatValue ?? 0;
                                  const totalWithVat =
                                    totalPrice * (1 + vatValue / 100);
                                  return {
                                    ...item,
                                    ...changes,
                                    quantity: qty,
                                    unitPrice: unitPrice,
                                    vatValue: vatValue,
                                    vatPercent: `${vatValue}%`,
                                    totalPriceCurr: `${formatDecimalToString(totalPrice.toFixed(2))} ${changes.currency || item.currency || bccyCurrency}`,
                                    totalAmountWithVatCurr: `${formatDecimalToString(totalWithVat.toFixed(2))} ${changes.currency || item.currency || bccyCurrency}`,
                                  };
                                }
                                return item;
                              });
                            });
                          }}
                          defaultEditingRowId={editingNewRowId || undefined}
                          onEditCancel={(rowId) => handleEditCancel(rowId)}
                          pageSize={50}
                          indentPerLevel={32}
                          maxDepth={3}
                          showGridLines={true}
                          showHover={true}
                          showToolbar={false}
                          className="project-item-grid"
                          showDeleteIcon={!editingNewRowId}
                          showInfoIcon={!editingNewRowId}
                          deleteConfirmationTitle="Remove Project Item"
                          deleteConfirmationMessage="Are you sure you want to delete this project item? This action cannot be undone."
                          onDeleteClick={(rowId: string) =>
                            handleDeleteRow(rowId)
                          }
                          onInfoClick={(rowId: string, row: any) => {
                            //console.log("Info click", rowId, row);
                            setSelectedInfoRow(row);
                            setInfoModalOpen(true);
                          }}
                          maxHeight={treeGridMaxHeight}
                        />
                      </div>
                    </Flex>
                  </Flex>
                </View>
              </View>

              {/* Add item and Multi add buttons below table - hidden when editing a new row */}
              {!editingNewRowId && (
                <Flex direction="row" gap="size-200" marginTop="size-225">
                  <Button
                    UNSAFE_className={"btn"}
                    variant="secondary"
                    onPress={handleAddItemClick}
                    UNSAFE_style={{
                      backgroundColor: "#fff",
                      color: "#0070f3",
                      border: "1px solid #0070f3",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontWeight: 600,
                    }}
                  >
                    <Text>
                      {t("add_item", {
                        ns: "layout_components",
                        defaultValue: "Add item",
                      })}
                    </Text>
                  </Button>
                  <Button
                    UNSAFE_className={"btn"}
                    variant="secondary"
                    onPress={() => setIsProductSelectionModalOpen(true)}
                    UNSAFE_style={{
                      backgroundColor: "#fff",
                      color: "#0070f3",
                      border: "1px solid #0070f3",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontWeight: 600,
                    }}
                  >
                    <Text>
                      {t("multi_add", {
                        ns: "layout_components",
                        defaultValue: "Multi add",
                      })}
                    </Text>
                  </Button>
                </Flex>
              )}

              <Flex
                direction={{ base: "column", S: "row" }}
                justifyContent="space-between"
                gap="size-200"
                marginTop="size-300"
                wrap="wrap"
              >
                {/* Left: Create Deal / Device / Project buttons */}
                <Flex gap="size-200" wrap="wrap">
                  <Button
                    variant="cta"
                    UNSAFE_className={"btn"}
                    UNSAFE_style={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      borderRadius: "8px",
                      borderColor: "#DFEFFD",
                    }}
                    onPress={handleCreateDeal}
                  >
                    <Text>
                      {selectedIds.length > 0
                        ? `${t("create_deal", { ns: "layout_components", defaultValue: "Create Deal" })} (${selectedIds.length})`
                        : t("create_deal", {
                            ns: "layout_components",
                            defaultValue: "Create Deal",
                          })}
                    </Text>
                  </Button>
                  <Button
                    UNSAFE_className={"btn"}
                    variant="cta"
                    UNSAFE_style={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      borderRadius: "8px",
                      borderColor: "#DFEFFD",
                    }}
                    onPress={handleCreateDevice}
                  >
                    <Text>
                      {selectedIds.length > 0
                        ? `${t("create_device", { ns: "layout_components", defaultValue: "Create Device" })} (${selectedIds.length})`
                        : t("create_device", {
                            ns: "layout_components",
                            defaultValue: "Create Device",
                          })}
                    </Text>
                  </Button>
                  <Button
                    UNSAFE_className={"btn"}
                    variant="cta"
                    UNSAFE_style={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      borderRadius: "8px",
                      borderColor: "#DFEFFD",
                    }}
                    onPress={handleCreateProject}
                  >
                    <Text>
                      {selectedIds.length > 0
                        ? `${t("create_project", { ns: "layout_components", defaultValue: "Create Project" })} (${selectedIds.length})`
                        : t("create_project", {
                            ns: "layout_components",
                            defaultValue: "Create Project",
                          })}
                    </Text>
                  </Button>
                </Flex>

                {/* Right: existing invoice document buttons */}
                <Flex gap="size-200" wrap="wrap">
                  <Button
                    UNSAFE_className={"btn"}
                    variant="cta"
                    UNSAFE_style={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      borderRadius: "8px",
                      borderColor: "#DFEFFD",
                    }}
                    onPress={handleCreateEstimate}
                  >
                    <Text>
                      {selectedIds.length > 0
                        ? `${t("create_estimate", { ns: "layout_components", defaultValue: "Create Estimate" })} (${selectedIds.length})`
                        : t("create_estimate", {
                            ns: "layout_components",
                            defaultValue: "Create Estimate",
                          })}
                    </Text>
                  </Button>
                  <Button
                    UNSAFE_className={"btn"}
                    variant="cta"
                    UNSAFE_style={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      borderRadius: "8px",
                      borderColor: "#DFEFFD",
                    }}
                    onPress={handleCreateSalesOrder}
                  >
                    <Text>
                      {selectedIds.length > 0
                        ? `${t("create_sales_order", { ns: "layout_components", defaultValue: "Create Sales Order" })} (${selectedIds.length})`
                        : t("create_sales_order", {
                            ns: "layout_components",
                            defaultValue: "Create Sales Order",
                          })}
                    </Text>
                  </Button>
                  <Button
                    UNSAFE_className={"btn"}
                    variant="cta"
                    UNSAFE_style={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      borderRadius: "8px",
                      borderColor: "#DFEFFD",
                    }}
                    onPress={handleCreateInvoice}
                  >
                    <Text>
                      {selectedIds.length > 0
                        ? `${t("create_invoice", { ns: "layout_components", defaultValue: "Create Invoice" })} (${selectedIds.length})`
                        : t("create_invoice", {
                            ns: "layout_components",
                            defaultValue: "Create Invoice",
                          })}
                    </Text>
                  </Button>
                </Flex>
              </Flex>
            </View>
          </View>
        </div>

        {/* Exchange Rate Update Dialog */}

        <DialogTrigger
          isOpen={showExchangeRateDialog}
          onOpenChange={setShowExchangeRateDialog}
        >
          <ActionButton UNSAFE_style={{ display: "none" }}>
            Hidden Trigger
          </ActionButton>
          {(close) => (
            <Dialog size="L">
              <Flex UNSAFE_className={styles.delete_pop_up_parent}>
                <Content UNSAFE_className={styles.delete_pop_up_header}>
                  {t("do_you_want_to_update_exchange_rate", {
                    ns: "layout_components",
                    defaultValue: "Do you want to update exchange rate?",
                  })}
                </Content>
                <View UNSAFE_className={styles.delete_pop_up_line}></View>
                <View UNSAFE_className={styles.delete_pop_up_warning_text}>
                  {t(
                    "if_you_agree_we_will_update_exchange_rate_of_the_deal_to_latest_system_stored_rate",
                    {
                      ns: "layout_components",
                      defaultValue:
                        "If you agree, we will update exchange rate of the deal to latest system stored rate.",
                    },
                  )}
                </View>
                <View UNSAFE_className={styles.buttons_parent}>
                  <Button
                    variant="secondary"
                    onPress={() => {
                      close();
                    }}
                    UNSAFE_className={styles.button_cancel}
                  >
                    <span className={styles.delete_pop_up_cancel_text}>
                      {t("cancel", {
                        ns: "layout_components",
                        defaultValue: "Cancel",
                      })}
                    </span>
                  </Button>
                  <Button
                    variant="primary"
                    onPress={() => {
                      handleExchangeRateDialogYes(false);
                      close();
                    }}
                    UNSAFE_className={styles.button_cancel}
                  >
                    <span className={styles.delete_pop_up_cancel_text}>
                      {t("no", { ns: "layout_components", defaultValue: "No" })}
                    </span>
                  </Button>
                  <Button
                    variant="negative"
                    onPress={() => {
                      handleExchangeRateDialogYes(true);
                      close();
                    }}
                    UNSAFE_className={styles.button_confirm}
                    >
                      <span className={styles.delete_pop_up_confirm_text}>
                        {t("yes", { ns: "common", defaultValue: "Yes" })}
                      </span>
                    </Button>
                </View>
                <View UNSAFE_className={styles.ellipse_div}>
                  <View UNSAFE_className={styles.delete_alert_icon}>
                    <AlertIconFill size={72} />
                  </View>
                </View>
              </Flex>
            </Dialog>
          )}
        </DialogTrigger>

        {/* Currency Rate Dialog */}
        <DialogTrigger
          isOpen={showCurrencyRateDialog}
          onOpenChange={setShowCurrencyRateDialog}
        >
          <ActionButton UNSAFE_style={{ display: "none" }}>
            Hidden Trigger
          </ActionButton>
          {(close) => (
            <Dialog>
              <Heading>
                {t("currency_rate", {
                  ns: "layout_components",
                  defaultValue: "Currency Rate",
                })}
              </Heading>
              <Divider />
              <DialogContent>
                <Flex direction="column" gap="size-200">
                  <Picker
                    label={t("currency", {
                      ns: "layout_components",
                      defaultValue: "Currency",
                    })}
                    UNSAFE_className={"picker"}
                    selectedKey={tempCurrency}
                    onSelectionChange={async (key) => {
                      setShowLoader(true);
                      dispatch({
                        type: "UPDATE_TEMP_CURRENCY",
                        currency: String(key),
                      });
                      await getTempCurrencyRate(
                        String(
                          currencies.find((cur: any) => cur.code === key)?.id,
                        ),
                        projectItemCalculation.finalDate + "T00:00:00",
                      );
                      setShowLoader(false);
                    }}
                    width="100%"
                  >
                    {currencies.map((cur: any) => (
                      <Item key={cur.code}>{cur.code}</Item>
                    ))}
                  </Picker>
                  <TextField
                    label={t("currency_rate", {
                      ns: "layout_components",
                      defaultValue: "Currency Rate",
                    })}
                    UNSAFE_className={"textField"}
                    value={tempRate}
                    onChange={(value) => {
                      dispatch({ type: "UPDATE_TEMP_RATE", rate: value });
                    }}
                    width="100%"
                  />
                  <Flex direction="row" gap="size-200" justifyContent="end">
                    <Button
                      variant="secondary"
                      onPress={() => {
                        handleCancelCurrencyRate();
                        close();
                      }}
                    >
                      {t("cancel", {
                        ns: "layout_components",
                        defaultValue: "Cancel",
                      })}
                    </Button>
                    <Button
                      variant="primary"
                      onPress={() => {
                        handleApplyCurrencyRate();
                        close();
                      }}
                    >
                      {t("apply", {
                        ns: "layout_components",
                        defaultValue: "Apply",
                      })}
                    </Button>
                  </Flex>
                </Flex>
              </DialogContent>
            </Dialog>
          )}
        </DialogTrigger>

        {/* Product Selection Modal for Multi-add */}
        <ProductSelectionModal
          isOpen={isProductSelectionModalOpen}
          onClose={() => setIsProductSelectionModalOpen(false)}
          onSave={handleMultiProductSave}
        />

        {/* Info Modal Dialog */}
        {selectedInfoRow && (
          <ItemCreateUpdate
            close={() => {
              setInfoModalOpen(false);
              setSelectedInfoRow(null);
            }}
            selectedItem={selectedItem}
            itemData={selectedInfoRow?.data}
            isCreateOpen={infoModalOpen}
            setIsCreateOpen={setInfoModalOpen}
            searchProducts={searchProducts}
            currencies={currencies}
            activeAttributesData={activeAttributesData}
            onSave={() => {
              loadProjectItemGridData();
              loadProjectItemCalculation(String(selectedItem?.id));
            }}
          />
        )}

        {/* Create Deal / Device / Project dialog */}
        <DialogContainer onDismiss={() => setCreateProjectDialogTarget(null)}>
          {createProjectDialogTarget !== null && selectedItem != null && (
            <ContextNewDialog
              parentItem={selectedItem}
              onClose={(reload) => {
                setCreateProjectDialogTarget(null);
                if (reload) {
                  loadProjectItemGridData();
                  loadProjectItemCalculation(String(selectedItem?.id));
                }
              }}
              additionalMode={true}
              additionalData={{
                fpaTypeCategory: projectListKeyRef.current,
                fpaType: "PROJECT",
                haveMasterActivity: false,
              }}
              mode="new"
              itemTypeTarget={createProjectDialogTarget.itemTypeTarget}
              prefilledItems={createProjectDialogTarget.items}
            />
          )}
        </DialogContainer>
      </>
    );
  }
}

export const ProjectItemGridComponent = LayoutComponent(ProjectItemGrid);
